import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { esSoloLecturaClaro, esContratista, BannerSoloLectura } from "./permisos";
import {
  LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, Legend, ReferenceLine, LabelList,
  ComposedChart, Area, AreaChart, ResponsiveContainer
} from "recharts";

// ── Paleta Claro Colombia ──
const C = {
  red:      "#E30613",
  redDark:  "#B0000E",
  redWine:  "#7f0010",
  navy:     "#0f172a",
  gray:     "#1e293b",
  slate:    "#475569",   // era #64748b — más oscuro para mejor legibilidad
  light:    "#64748b",   // era #94a3b8 — más oscuro para mejor legibilidad
  bg:       "#f1f5f9",
  white:    "#ffffff",
  green:    "#15803d",
  greenMid: "#16a34a",
  amber:    "#ca8a04",
  orange:   "#ea580c",
  silver:     "#c4c8cc",  // plateado — neutro, sin matiz azul
  silverDark: "#6b7177",  // plateado oscuro — neutro, sin matiz azul
  black:      "#000000",
};

const CLASIFICACIONES = [
  { cat: "N0", label: "N0", dispMin: 100,  dispMax: Infinity, color: C.green,    texto: "#fff" },
  { cat: "N1", label: "N1", dispMin: 99.2, dispMax: 100,      color: C.greenMid, texto: "#fff" },
  { cat: "N2", label: "N2", dispMin: 98.3, dispMax: 99.2,     color: C.amber,    texto: "#fff" },
  { cat: "N3", label: "N3", dispMin: 96.7, dispMax: 98.3,     color: C.orange,   texto: "#fff" },
  { cat: "N4", label: "N4", dispMin: 94.7, dispMax: 96.7,     color: C.red,      texto: "#fff" },
  { cat: "N5", label: "N5", dispMin: 85,   dispMax: 94.7,     color: C.redDark,  texto: "#fff" },
  { cat: "N6", label: "N6", dispMin: 0,    dispMax: 85,        color: C.redWine,  texto: "#fff" },
];

const CATEGORIAS_N2MAS = ["N2", "N3", "N4", "N5", "N6"];

// ── Meta corporativa del indicador de Disponibilidad (Red Fija, Región 3) ──
// Tomada del panel oficial "Indicadores Técnicos Claro - Red Fija" (reportinggir.claro.com.co).
// Se usa como línea de referencia en la gráfica "Total ofensores N2+ por mes", para
// comparar el % de nodos sin impacto contra la meta que la compañía tiene fijada mes a mes.
const METAS_DISPONIBILIDAD = {
  "11-2025": 92.69, "12-2025": 92.85,
  "01-2026": 93.02, "02-2026": 93.03, "03-2026": 94.46, "04-2026": 95.72,
  "05-2026": 97.20, "06-2026": 98.89, "07-2026": 98.89, "08-2026": 98.03,
  "09-2026": 98.81, "10-2026": 98.27,
};

function diasDelMes(mesAnio) {
  if (!mesAnio) return 30;
  const [mes, anio] = mesAnio.split("-").map(Number);
  return new Date(anio, mes, 0).getDate();
}

function fmtHMin(horas) {
  if (horas <= 0) return "0 h 0 min";
  const h = Math.floor(horas);
  const m = Math.round((horas - h) * 60);
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
}

function fmtMin(horas) {
  const minutos = horas * 60;
  return minutos.toLocaleString("es-CO", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " min";
}

function mismoMesAnioAnterior(mesAnio) {
  if (!mesAnio) return null;
  const [mes, anio] = mesAnio.split("-").map(Number);
  return `${String(mes).padStart(2, "0")}-${anio - 1}`;
}

// ── Regresión lineal simple ──
function regresionLineal(arr) {
  const n = arr.length;
  if (n < 2) return null;
  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
  arr.forEach((y, x) => { sumX += x; sumY += y; sumXY += x * y; sumX2 += x * x; });
  const pendiente = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercepto = (sumY - pendiente * sumX) / n;
  return { pendiente, intercepto, proyectar: (x) => intercepto + pendiente * x };
}

// ── Promedio móvil ──
function promedioMovil(arr, ventana = 3) {
  return arr.map((_, i) => {
    if (i < ventana - 1) return null;
    const slice = arr.slice(i - ventana + 1, i + 1);
    return Math.round(slice.reduce((a, b) => a + b, 0) / ventana);
  });
}


// ── Checkbox visual ──────────────────────────────────────────────────────
function Checkbox({ checked, indeterminate }) {
  return (
    <div style={{ width: "13px", height: "13px", borderRadius: "3px", flexShrink: 0,
      border: `2px solid ${checked || indeterminate ? "#E30613" : "#94a3b8"}`,
      background: checked ? "#E30613" : "white",
      display: "flex", alignItems: "center", justifyContent: "center" }}>
      {checked && <span style={{ color: "white", fontSize: "8px", fontWeight: "800", lineHeight: 1 }}>✓</span>}
      {!checked && indeterminate && <span style={{ color: "#E30613", fontSize: "10px", fontWeight: "800", lineHeight: 1 }}>—</span>}
    </div>
  );
}

// ── Dropdown multiselect — DOM puro, inmune a re-renders ─────────────────
// valor: null=todos, []=ninguno, [a,b]=seleccionados
function DropdownMulti({ label, opciones, valor, onChange, placeholder = "Todos" }) {
  const containerRef      = useRef(null);
  const panelRef          = useRef(null);
  const isOpen            = useRef(false);
  const opcsCongeladas    = useRef(opciones);

  const todosMarcados  = valor === null;
  const ningunoMarcado = Array.isArray(valor) && valor.length === 0;
  const selCount       = todosMarcados ? opciones.length : (Array.isArray(valor) ? valor.length : 0);
  const hayFiltro      = !todosMarcados;

  const texto = todosMarcados ? placeholder
    : ningunoMarcado ? "(Ninguno)"
    : selCount <= 2 ? (Array.isArray(valor) ? valor.join(", ") : "")
    : `${selCount} selec.`;

  const openPanel = () => {
    opcsCongeladas.current = opciones;
    buildPanel(opciones, valor);
    isOpen.current = true;
    if (panelRef.current) panelRef.current.style.display = "block";
  };
  const closePanel = () => {
    isOpen.current = false;
    if (panelRef.current) panelRef.current.style.display = "none";
  };
  const togglePanel = () => isOpen.current ? closePanel() : openPanel();

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) closePanel();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Reconstruir panel solo cuando cambia valor (opciones congeladas mientras está abierto)
  useEffect(() => {
    if (!isOpen.current) return;
    buildPanel(opcsCongeladas.current, valor);
    if (panelRef.current) panelRef.current.style.display = "block";
  }, [valor]);

  function buildPanel(opts, val) {
    const panel = panelRef.current;
    if (!panel) return;
    panel.innerHTML = "";
    const todosM   = val === null;
    const parcialM = Array.isArray(val) && val.length > 0 && val.length < opts.length;

    // Fila "Seleccionar todo"
    const rowAll = document.createElement("div");
    rowAll.style.cssText = "display:flex;align-items:center;gap:8px;padding:6px 12px;cursor:pointer;border-bottom:1px solid #f1f5f9;user-select:none;font-size:12px;color:#0f172a;font-weight:600;";
    rowAll.onmouseenter = () => rowAll.style.background = "#f8fafc";
    rowAll.onmouseleave = () => rowAll.style.background = "white";
    rowAll.onclick = (e) => { e.stopPropagation(); onChange(todosM ? [] : null); };
    const chkAll = document.createElement("div");
    chkAll.style.cssText = `width:13px;height:13px;border-radius:3px;flex-shrink:0;border:2px solid ${todosM||parcialM?"#E30613":"#94a3b8"};background:${todosM?"#E30613":"white"};display:flex;align-items:center;justify-content:center;font-size:8px;font-weight:800;color:white;`;
    if (todosM) chkAll.textContent = "✓";
    if (!todosM && parcialM) { chkAll.style.color = "#E30613"; chkAll.textContent = "—"; }
    rowAll.appendChild(chkAll);
    rowAll.appendChild(document.createTextNode("(Seleccionar todo)"));
    panel.appendChild(rowAll);

    opts.forEach(op => {
      const row = document.createElement("div");
      row.style.cssText = "display:flex;align-items:center;gap:8px;padding:5px 12px;cursor:pointer;user-select:none;font-size:12px;color:#0f172a;";
      row.onmouseenter = () => row.style.background = "#f8fafc";
      row.onmouseleave = () => row.style.background = "white";
      row.onclick = (e) => {
        e.stopPropagation();
        if (todosM) { onChange(opts.filter(x => x !== op)); return; }
        if (Array.isArray(val)) {
          const next = val.includes(op) ? val.filter(x => x !== op) : [...val, op];
          onChange(next.length === opts.length ? null : next);
        }
      };
      const marcado = val === null || (Array.isArray(val) && val.includes(op));
      const chk = document.createElement("div");
      chk.style.cssText = `width:13px;height:13px;border-radius:3px;flex-shrink:0;border:2px solid ${marcado?"#E30613":"#94a3b8"};background:${marcado?"#E30613":"white"};display:flex;align-items:center;justify-content:center;font-size:8px;font-weight:800;color:white;`;
      if (marcado) chk.textContent = "✓";
      row.appendChild(chk);
      row.appendChild(document.createTextNode(op));
      panel.appendChild(row);
    });
  }

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <label style={{ fontWeight: "500", fontSize: "12px", color: "#475569", marginRight: "6px" }}>{label}</label>
      <div onClick={togglePanel}
        style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "6px 10px",
          borderRadius: "6px", border: hayFiltro ? "1.5px solid #E30613" : "1px solid #e2e8f0",
          fontSize: "12px", color: hayFiltro ? "#0f172a" : "#94a3b8", background: "white",
          cursor: "pointer", minWidth: "100px", justifyContent: "space-between" }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "160px" }}>{texto}</span>
        {hayFiltro && (
          <span style={{ background: "#E30613", color: "white", borderRadius: "10px", padding: "0 5px", fontSize: "9px", fontWeight: "700", flexShrink: 0 }}>
            {selCount}
          </span>
        )}
        <span style={{ fontSize: "9px", color: "#64748b", flexShrink: 0 }}>▼</span>
      </div>
      <div ref={panelRef} style={{ display: "none", position: "absolute", top: "calc(100% + 3px)", left: 0, zIndex: 9999,
        background: "white", border: "1px solid #cbd5e1", borderRadius: "8px",
        boxShadow: "0 6px 20px rgba(0,0,0,0.15)", minWidth: "100%", maxHeight: "260px",
        overflowY: "auto", padding: "4px 0" }} />
    </div>
  );
}

function Home({ usuario }) {
  const soloLectura = esSoloLecturaClaro(usuario) || esContratista(usuario);
  const [data, setData] = useState([]);
  const [categoriaData, setCategoriaData] = useState([]);
  const [jefeData, setJefeData] = useState([]);
  const [resumenData, setResumenData] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [mesSeleccionado, setMesSeleccionado] = useState(""); // mes "Hasta" del rango
  const [mesDesdeFiltro, setMesDesdeFiltro] = useState(""); // mes "Desde" del rango ("" = sin límite inferior)
  const [mesesDisponibles, setMesesDisponibles] = useState([]);
  // ── Dos meses puntuales a comparar en "Resumen Disponibilidad" (junto a Fin), igual que en Ranking Jefes ──
  const [mesCompA, setMesCompA] = useState("");
  const [mesCompB, setMesCompB] = useState("");
  // ── Modo de filtro de fechas: "rango" usa Inicio/Fin, "comparar" usa Comparar A vs B. Son mutuamente excluyentes. ──
  const [modoFiltroFecha, setModoFiltroFecha] = useState("rango"); // "rango" | "comparar"
  const [filtroJefe, setFiltroJefe]           = useState(null); // null=todos, []=ninguno, [a,b]=seleccionados
  const [filtroAliado, setFiltroAliado]       = useState(null);
  const [filtroCiudad, setFiltroCiudad]       = useState(null);
  const [filtroPrioridad, setFiltroPrioridad] = useState(null);
  const [cortesDelMesAnterior, setCortesDelMesAnterior] = useState([]);
  const [vistaJefes, setVistaJefes] = useState("mes");
  // ── Medición real de la gráfica "Total ofensores N2+ por mes", para alinear
  //    pixel a pixel la tabla de resumen de abajo con cada mes de la gráfica ──
  const chartTotalWrapRef = useRef(null);
  const [chartTotalTicksX, setChartTotalTicksX] = useState([]);
  const [chartTotalAncho, setChartTotalAncho] = useState(0);

  // ── Mes "activo" que gobierna el Resumen Disponibilidad: en modo "comparar" es el mes A elegido; en modo "rango" es "Fin" ──
  const mesActivo = (modoFiltroFecha === "comparar" && mesCompA) ? mesCompA : mesSeleccionado;

  const formatearFecha = (fecha) => {
    if (!fecha) return "";
    const f = fecha.includes("T") ? fecha.split("T")[0] : fecha;
    const [anio, mes, dia] = f.split("-");
    return `${dia}/${mes}/${anio}`;
  };

  const formatearMes = (fecha) => {
    if (!fecha) return "";
    if (fecha.includes("T")) {
      const [anio, mes] = fecha.split("T")[0].split("-");
      return `${mes}-${anio}`;
    }
    if (fecha.includes("-") && fecha.length === 10) {
      const [anio, mes] = fecha.split("-");
      return `${mes}-${anio}`;
    }
    return fecha;
  };

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/ofensores?rol=${encodeURIComponent(usuario?.rol||"")}&cedula=${encodeURIComponent(usuario?.cedula||"")}`)
      .then(res => res.json())
      .then(data => {
        const normalizarMes = (fecha) => {
          if (!fecha) return "";
          if (fecha.includes("T")) {
            const [anio, mes] = fecha.split("T")[0].split("-");
            return `${mes}-${anio}`;
          }
          const partes = fecha.split("/");
          if (partes.length === 3) return `${partes[1].padStart(2,"0")}-${partes[2]}`;
          return fecha;
        };
        const parseFecha = (str) => {
          const [mes, anio] = str.split("-").map(Number);
          return new Date(anio, mes - 1);
        };

        const agrupado = {};
        data.forEach(item => {
          const cat = (item.categoria || "").trim();
          if (!CATEGORIAS_N2MAS.includes(cat)) return;
          const mes = normalizarMes(item.mes_anio);
          if (!agrupado[mes]) agrupado[mes] = { mes, total: 0, alto: 0, medio: 0 };
          agrupado[mes].total++;
          const p = (item.tipo_prioridad || item.prioridad || "").toString().toLowerCase();
          if (p.includes("alto") || p.includes("alta")) agrupado[mes].alto++;
          if (p.includes("medio") || p.includes("media")) agrupado[mes].medio++;
        });
        setData(Object.values(agrupado).sort((a,b) => parseFecha(a.mes) - parseFecha(b.mes)));

        const catMap = {};
        data.forEach(item => {
          const cat = (item.categoria || "").trim();
          if (!CATEGORIAS_N2MAS.includes(cat)) return;
          const mes = normalizarMes(item.mes_anio);
          if (!catMap[mes]) catMap[mes] = { mes };
          catMap[mes][cat] = (catMap[mes][cat] || 0) + 1;
        });
        setCategoriaData(Object.values(catMap).sort((a,b) => parseFecha(a.mes) - parseFecha(b.mes)));

        const jefeMap = {};
        data.forEach(item => {
          const cat = (item.categoria || "").trim();
          if (!CATEGORIAS_N2MAS.includes(cat)) return;
          const mes = normalizarMes(item.mes_anio);
          const jefe = item.nombre_jefe || "Sin jefe";
          if (!jefeMap[mes]) jefeMap[mes] = { mes };
          jefeMap[mes][jefe] = (jefeMap[mes][jefe] || 0) + 1;
        });
        setJefeData(Object.values(jefeMap).sort((a,b) => parseFecha(a.mes) - parseFecha(b.mes)));
        setRawData(data);
      })
      .catch(err => console.error(err));
  }, [usuario]);

  useEffect(() => {
    if (rawData.length === 0) return;
    const normalizarMes = (fecha) => {
      if (!fecha) return "";
      if (fecha.includes("T")) { const [anio, mes] = fecha.split("T")[0].split("-"); return `${mes}-${anio}`; }
      const partes = fecha.split("/");
      if (partes.length === 3) return `${partes[1].padStart(2,"0")}-${partes[2]}`;
      return fecha;
    };
    const parseFecha = (str) => { const [mes, anio] = str.split("-").map(Number); return new Date(anio, mes - 1); };
    const mesToYYYYMM = (str) => { const [mes, anio] = str.split("-").map(Number); return `${anio}-${String(mes).padStart(2,"0")}`; };

    const filtrado = rawData.filter(item => {
      const mesMM_YYYY = normalizarMes(item.mes_anio);
      const mesYYYYMM  = mesMM_YYYY ? mesToYYYYMM(mesMM_YYYY) : "";
      let pasaMes;
      if (modoFiltroFecha === "comparar") {
        // Modo "Comparar A vs B" activo: Inicio/Fin quedan inhabilitados y no se aplican
        const compAYYYYMM = mesCompA ? mesToYYYYMM(mesCompA) : "";
        const compBYYYYMM = mesCompB ? mesToYYYYMM(mesCompB) : "";
        const compMinYYYYMM = compAYYYYMM && compBYYYYMM ? (compAYYYYMM <= compBYYYYMM ? compAYYYYMM : compBYYYYMM) : (compAYYYYMM || compBYYYYMM);
        const compMaxYYYYMM = compAYYYYMM && compBYYYYMM ? (compAYYYYMM >= compBYYYYMM ? compAYYYYMM : compBYYYYMM) : (compAYYYYMM || compBYYYYMM);
        pasaMes = (!compMinYYYYMM || mesYYYYMM >= compMinYYYYMM) && (!compMaxYYYYMM || mesYYYYMM <= compMaxYYYYMM);
      } else {
        // Modo "rango" activo: Comparar A/B queda inhabilitado y no se aplica
        const limiteYYYYMM = mesSeleccionado ? mesToYYYYMM(mesSeleccionado) : "";
        const limiteDesdeYYYYMM = mesDesdeFiltro ? mesToYYYYMM(mesDesdeFiltro) : "";
        const pasaMesHasta = !limiteYYYYMM || mesYYYYMM <= limiteYYYYMM;
        const pasaMesDesde = !limiteDesdeYYYYMM || mesYYYYMM >= limiteDesdeYYYYMM;
        pasaMes = pasaMesHasta && pasaMesDesde;
      }
      const pasaJefe      = filtroJefe === null || (Array.isArray(filtroJefe) && (filtroJefe.length === 0 || filtroJefe.includes((item.nombre_jefe || "").trim())));
      const pasaAliado    = filtroAliado === null || (Array.isArray(filtroAliado) && (filtroAliado.length === 0 || filtroAliado.includes((item.aliado || "").trim())));
      const pasaCiudad    = filtroCiudad === null || (Array.isArray(filtroCiudad) && (filtroCiudad.length === 0 || filtroCiudad.includes((item.ciudad || "").trim())));
      const pRaw          = (item.tipo_prioridad || item.prioridad || "").toString().toLowerCase();
      const pNorm         = pRaw.includes("alto") || pRaw.includes("alta") ? "Alta" : pRaw.includes("medio") || pRaw.includes("media") ? "Media" : "Otra";
      const pasaPrioridad = filtroPrioridad === null || (Array.isArray(filtroPrioridad) && (filtroPrioridad.length === 0 || filtroPrioridad.includes(pNorm)));
      return pasaMes && pasaJefe && pasaAliado && pasaCiudad && pasaPrioridad;
    });

    const agrupado = {};
    filtrado.forEach(item => {
      const cat = (item.categoria || "").trim();
      if (!CATEGORIAS_N2MAS.includes(cat)) return;
      const mes = normalizarMes(item.mes_anio);
      if (!agrupado[mes]) agrupado[mes] = { mes, total: 0, alto: 0, medio: 0 };
      agrupado[mes].total++;
      const p = (item.tipo_prioridad || item.prioridad || "").toString().toLowerCase();
      if (p.includes("alto") || p.includes("alta")) agrupado[mes].alto++;
      if (p.includes("medio") || p.includes("media")) agrupado[mes].medio++;
    });
    setData(Object.values(agrupado).sort((a,b) => parseFecha(a.mes) - parseFecha(b.mes)));

    const catMap = {};
    filtrado.forEach(item => {
      const cat = (item.categoria || "").trim();
      if (!CATEGORIAS_N2MAS.includes(cat)) return;
      const mes = normalizarMes(item.mes_anio);
      if (!catMap[mes]) catMap[mes] = { mes };
      catMap[mes][cat] = (catMap[mes][cat] || 0) + 1;
    });
    setCategoriaData(Object.values(catMap).sort((a,b) => parseFecha(a.mes) - parseFecha(b.mes)));

    const jefeMap = {};
    filtrado.forEach(item => {
      const cat = (item.categoria || "").trim();
      if (!CATEGORIAS_N2MAS.includes(cat)) return;
      const mes = normalizarMes(item.mes_anio);
      const jefe = item.nombre_jefe || "Sin jefe";
      if (!jefeMap[mes]) jefeMap[mes] = { mes };
      jefeMap[mes][jefe] = (jefeMap[mes][jefe] || 0) + 1;
    });
    setJefeData(Object.values(jefeMap).sort((a,b) => parseFecha(a.mes) - parseFecha(b.mes)));
  }, [rawData, mesSeleccionado, mesDesdeFiltro, mesCompA, mesCompB, modoFiltroFecha, filtroJefe, filtroAliado, filtroCiudad, filtroPrioridad]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/dashboard-resumen?rol=${encodeURIComponent(usuario?.rol||"")}&cedula=${encodeURIComponent(usuario?.cedula||"")}`)
      .then(res => res.json())
      .then(rows => {
        setResumenData(rows);
        const meses = [...new Set(rows.map(r => formatearMes(r.m_fecha_mes)))]
          .filter(Boolean)
          .sort((a, b) => {
            const [mesA, anioA] = a.split("-").map(Number);
            const [mesB, anioB] = b.split("-").map(Number);
            return new Date(anioB, mesB - 1) - new Date(anioA, mesA - 1);
          });
        setMesesDisponibles(meses);
        if (meses.length > 0) setMesSeleccionado(meses[0]);
        // Por defecto, el filtro "Inicio" arranca en enero-2026 (si existe en los
        // datos); si no existe, se deja sin límite inferior (Todos).
        if (meses.includes("01-2026")) setMesDesdeFiltro("01-2026");
      })
      .catch(err => console.error(err));
  }, [usuario]);

  // Cargar TODOS los cortes del mes anterior cuando cambia el mes activo (Fin en modo rango, o mes A en modo comparar)
  useEffect(() => {
    if (!mesActivo || !usuario) return;
    // Convertir MM-YYYY → YYYY-MM para el API
    const [mm, yyyy] = mesActivo.split("-");
    // Calcular mes anterior
    const fecha = new Date(parseInt(yyyy), parseInt(mm) - 2, 1); // -2 porque mes es 1-indexed y queremos el anterior
    const mesAnt = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, "0")}`;
    fetch(`${process.env.REACT_APP_API_URL}/dashboard-cortes-mes-anterior?rol=${encodeURIComponent(usuario?.rol||"")}&cedula=${encodeURIComponent(usuario?.cedula||"")}&mes_anterior=${mesAnt}`)
      .then(res => res.json())
      .then(rows => setCortesDelMesAnterior(rows))
      .catch(err => console.error(err));
  }, [mesActivo, usuario]);

  // ── Inicializar/mantener válidos los dos meses a comparar en Resumen Disponibilidad ──
  useEffect(() => {
    if (mesesDisponibles.length === 0) return;
    setMesCompA(prev => (prev && mesesDisponibles.includes(prev)) ? prev : mesesDisponibles[0]);
    setMesCompB(prev => (prev && mesesDisponibles.includes(prev)) ? prev : (mesesDisponibles[1] || mesesDisponibles[0]));
  }, [mesesDisponibles]);

  // ── Opciones bidireccionales: cada filtro muestra solo lo que existe
  //    considerando todos los demás filtros activos (excepto el suyo) ──────
  const optsJefe = useMemo(() => {
    const base = rawData.filter(d => {
      const pasaAliado = !filtroAliado || filtroAliado === null || (Array.isArray(filtroAliado) && (filtroAliado.length === 0 || filtroAliado.includes((d.aliado || "").trim())));
      const pasaCiudad = !filtroCiudad || filtroCiudad === null || (Array.isArray(filtroCiudad) && (filtroCiudad.length === 0 || filtroCiudad.includes((d.ciudad || "").trim())));
      return pasaAliado && pasaCiudad;
    });
    return [...new Set(base.map(d => (d.nombre_jefe || "").trim()).filter(Boolean))].sort();
  }, [rawData, filtroAliado, filtroCiudad]);

  const optsAliado = useMemo(() => {
    const base = rawData.filter(d => {
      const pasaJefe   = !filtroJefe   || filtroJefe   === null || (Array.isArray(filtroJefe)   && (filtroJefe.length   === 0 || filtroJefe.includes((d.nombre_jefe || "").trim())));
      const pasaCiudad = !filtroCiudad || filtroCiudad === null || (Array.isArray(filtroCiudad) && (filtroCiudad.length === 0 || filtroCiudad.includes((d.ciudad || "").trim())));
      return pasaJefe && pasaCiudad;
    });
    return [...new Set(base.map(d => (d.aliado || "").trim()).filter(Boolean))].sort();
  }, [rawData, filtroJefe, filtroCiudad]);

  const optsCiudad = useMemo(() => {
    const base = rawData.filter(d => {
      const pasaJefe   = !filtroJefe   || filtroJefe   === null || (Array.isArray(filtroJefe)   && (filtroJefe.length   === 0 || filtroJefe.includes((d.nombre_jefe || "").trim())));
      const pasaAliado = !filtroAliado || filtroAliado === null || (Array.isArray(filtroAliado) && (filtroAliado.length === 0 || filtroAliado.includes((d.aliado || "").trim())));
      return pasaJefe && pasaAliado;
    });
    return [...new Set(base.map(d => (d.ciudad || "").trim()).filter(Boolean))].sort();
  }, [rawData, filtroJefe, filtroAliado]);

  // Opciones fijas de prioridad
  const optsPrioridad = ["Alta", "Media"];

  // ── Métricas gerenciales calculadas de "data" (serie histórica filtrada) ──
  const metricsGerenciales = (() => {
    if (data.length < 2) return null;
    const totales = data.map(d => d.total);
    const reg = regresionLineal(totales);
    const promovil = promedioMovil(totales, 3);
    const promedio = Math.round(totales.reduce((a, b) => a + b, 0) / totales.length);
    const max = Math.max(...totales);
    const min = Math.min(...totales);

    // Mejor y peor mes
    const mejorMes = data[totales.indexOf(min)];
    const peorMes  = data[totales.indexOf(max)];

    // Tendencia
    const tendencia = reg ? reg.pendiente : 0;
    const labelTendencia = tendencia > 1 ? "Alza 📈" : tendencia < -1 ? "Baja 📉" : "Estable ➡️";
    const colorTendencia = tendencia > 1 ? C.red : tendencia < -1 ? C.greenMid : C.amber;

    // Mayor carga actual (último mes)
    const ultimoJefeData = jefeData[jefeData.length - 1] || {};
    const jefeKeys = Object.keys(ultimoJefeData).filter(k => k !== "mes");
    const mayorCargaJefe = jefeKeys.length > 0
      ? jefeKeys.reduce((a, b) => (ultimoJefeData[a] || 0) > (ultimoJefeData[b] || 0) ? a : b, jefeKeys[0])
      : null;
    const mayorCargaVal = mayorCargaJefe ? ultimoJefeData[mayorCargaJefe] : 0;

    // Semáforo por jefe: comparar último mes vs penúltimo
    const ultimoMes = jefeData[jefeData.length - 1] || {};
    const penultimoMes = jefeData[jefeData.length - 2] || {};
    const todosJefes = [...new Set([
      ...Object.keys(ultimoMes).filter(k => k !== "mes"),
      ...Object.keys(penultimoMes).filter(k => k !== "mes")
    ])].sort();
    const semaforoJefes = todosJefes.map(jefe => {
      const actual = ultimoMes[jefe] || 0;
      const anterior = penultimoMes[jefe] || 0;
      const delta = actual - anterior;
      const pct = anterior > 0 ? ((delta / anterior) * 100).toFixed(1) : null;
      let estado, color, icon;
      if (delta > 0)      { estado = "Empeoró"; color = C.red;      icon = "🔴"; }
      else if (delta < 0) { estado = "Mejoró";  color = C.greenMid; icon = "🟢"; }
      else                { estado = "Igual";   color = C.amber;    icon = "🟡"; }
      return { jefe, actual, anterior, delta, pct, estado, color, icon };
    });

    // Ranking de jefes por mes más reciente (menor cantidad de ofensores = mejor posición)
    const rankingJefes = [...semaforoJefes].sort((a, b) => a.actual - b.actual);

    // Línea de tendencia para gráfica (puntos proyectados sobre los mismos meses)
    const lineaTendencia = reg
      ? data.map((d, i) => ({ ...d, tendencia: Math.max(0, Math.round(reg.proyectar(i))) }))
      : data;

    // Promedio móvil enriquecido
    const dataConMovil = data.map((d, i) => ({
      ...d,
      promedioMovil: promovil[i],
      promedioGeneral: promedio,
      tendencia: reg ? Math.max(0, Math.round(reg.proyectar(i))) : null,
    }));

    return {
      tendencia, labelTendencia, colorTendencia,
      promedio, max, min, mejorMes, peorMes,
      mayorCargaJefe, mayorCargaVal,
      semaforoJefes, rankingJefes,
      lineaTendencia, dataConMovil,
      mesesAnalizados: data.length,
    };
  })();

  const tablaResumen = (() => {
    if (!mesActivo || resumenData.length === 0) return null;
    const todosMeses = [...new Set(resumenData.map(r => formatearMes(r.m_fecha_mes)))]
      .filter(Boolean)
      .sort((a, b) => {
        const [mesA, anioA] = a.split("-").map(Number);
        const [mesB, anioB] = b.split("-").map(Number);
        return new Date(anioB, mesB - 1) - new Date(anioA, mesA - 1);
      });
    const idxActual    = todosMeses.indexOf(mesActivo);
    // En modo "comparar", el mes de referencia es el mes B elegido por el usuario
    // (no el mes calendario inmediatamente anterior), para que las tarjetas y la
    // tabla coincidan exactamente con la comparación que el usuario armó arriba.
    const mesAnterior  = modoFiltroFecha === "comparar" ? (mesCompB || null) : (todosMeses[idxActual + 1] || null);
    const mesAnioAnteriorLabel  = mismoMesAnioAnterior(mesActivo);
    const mesAnioAnteriorExiste = todosMeses.includes(mesAnioAnteriorLabel);

    const filtrarPorMes = (mes) => {
      const filas = resumenData.filter(r => {
        const coincideMes    = formatearMes(r.m_fecha_mes) === mes;
        const coincideJefe      = filtroJefe === null || (Array.isArray(filtroJefe) && (filtroJefe.length === 0 || filtroJefe.includes((r.nombre_jefe || "").trim())));
        const coincideAliado    = filtroAliado === null || (Array.isArray(filtroAliado) && (filtroAliado.length === 0 || filtroAliado.includes((r.aliado || "").trim())));
        const coincideCiudad    = filtroCiudad === null || (Array.isArray(filtroCiudad) && (filtroCiudad.length === 0 || filtroCiudad.includes((r.ciudad || "").trim())));
        const pRawR             = (r.tipo_prioridad || r.prioridad || "").toString().toLowerCase();
        const pNormR            = pRawR.includes("alto") || pRawR.includes("alta") ? "Alta" : pRawR.includes("medio") || pRawR.includes("media") ? "Media" : "Otra";
        const coincidePrioridad = filtroPrioridad === null || (Array.isArray(filtroPrioridad) && (filtroPrioridad.length === 0 || filtroPrioridad.includes(pNormR)));
        return coincideMes && coincideJefe && coincideAliado && coincideCiudad && coincidePrioridad;
      });
      if (filas.length === 0) return { nodos: [], fechaCorte: null };
      const ultimaFecha = filas.reduce((max, r) => {
        const f = r.fecha_corte.includes("T") ? r.fecha_corte.split("T")[0] : r.fecha_corte;
        return f > max ? f : max;
      }, "");
      const nodos = filas.filter(r => {
        const f = r.fecha_corte.includes("T") ? r.fecha_corte.split("T")[0] : r.fecha_corte;
        return f === ultimaFecha;
      });
      return { nodos, fechaCorte: ultimaFecha };
    };

    const actual       = filtrarPorMes(mesActivo);
    const anterior     = mesAnterior ? filtrarPorMes(mesAnterior) : { nodos: [], fechaCorte: null };
    const anioAnterior = mesAnioAnteriorExiste ? filtrarPorMes(mesAnioAnteriorLabel) : { nodos: [], fechaCorte: null };

    // ── Corte más cercano del mes anterior usando cortesDelMesAnterior ───────
    // cortesDelMesAnterior tiene TODOS los cortes del mes anterior (no solo el último)
    const corteMasCercanoMesAnterior = (() => {
      if (!actual.fechaCorte || cortesDelMesAnterior.length === 0) return { nodos: [], fechaCorte: null };

      const fechaActualStr = actual.fechaCorte.split("T")[0];
      const [anioAct, mesAct, diaActStr] = fechaActualStr.split("-");
      const diaActual = parseInt(diaActStr);
      const diasEnMesActual = new Date(parseInt(anioAct), parseInt(mesAct), 0).getDate();
      const esUltimoDiaMes = diaActual === diasEnMesActual;

      // Obtener todos los cortes distintos del mes anterior ordenados
      const cortesUnicos = [...new Set(
        cortesDelMesAnterior.map(r => r.fecha_corte.includes("T") ? r.fecha_corte.split("T")[0] : r.fecha_corte)
      )].sort();

      if (cortesUnicos.length === 0) return { nodos: [], fechaCorte: null };

      let corteMasCercano;

      if (esUltimoDiaMes) {
        // Si el corte actual es el último día del mes → tomar el último corte del mes anterior
        corteMasCercano = cortesUnicos[cortesUnicos.length - 1];
      } else {
        // Buscar el corte cuyo día sea más cercano al diaActual
        corteMasCercano = cortesUnicos.reduce((mejor, fc) => {
          const dia = parseInt(fc.split("-")[2]);
          const diffActual = Math.abs(dia - diaActual);
          const diffMejor  = Math.abs(parseInt(mejor.split("-")[2]) - diaActual);
          return diffActual < diffMejor ? fc : mejor;
        }, cortesUnicos[0]);
      }

      // Filtrar nodos de ese corte con los filtros activos
      const nodos = cortesDelMesAnterior.filter(r => {
        const fc = r.fecha_corte.includes("T") ? r.fecha_corte.split("T")[0] : r.fecha_corte;
        const coincideJefe   = filtroJefe === null || (Array.isArray(filtroJefe) && (filtroJefe.length === 0 || filtroJefe.includes((r.nombre_jefe || "").trim())));
        const coincideAliado = filtroAliado === null || (Array.isArray(filtroAliado) && (filtroAliado.length === 0 || filtroAliado.includes((r.aliado || "").trim())));
        const coincideCiudad = filtroCiudad === null || (Array.isArray(filtroCiudad) && (filtroCiudad.length === 0 || filtroCiudad.includes((r.ciudad || "").trim())));
        const pRawC          = (r.tipo_prioridad || r.prioridad || "").toString().toLowerCase();
        const pNormC         = pRawC.includes("alto") || pRawC.includes("alta") ? "Alta" : pRawC.includes("medio") || pRawC.includes("media") ? "Media" : "Otra";
        const coincidePrio   = filtroPrioridad === null || (Array.isArray(filtroPrioridad) && (filtroPrioridad.length === 0 || filtroPrioridad.includes(pNormC)));
        return fc === corteMasCercano && coincideJefe && coincideAliado && coincideCiudad && coincidePrio;
      });
      return { nodos, fechaCorte: corteMasCercano };
    })();

    const contarPorCat = (nodos) => {
      const counts = {};
      CLASIFICACIONES.forEach(c => counts[c.cat] = 0);
      nodos.forEach(n => {
        const cat = (n.categoria || "").trim();
        if (cat && counts[cat] !== undefined) counts[cat]++;
      });
      return counts;
    };

    const countActual       = contarPorCat(actual.nodos);
    const countAnterior     = contarPorCat(anterior.nodos);
    const countAnioAnterior = contarPorCat(anioAnterior.nodos);
    const countCorteCercano = contarPorCat(corteMasCercanoMesAnterior.nodos);

    const totalActual       = actual.nodos.length;
    const totalAnterior     = anterior.nodos.length;
    const totalAnioAnterior = anioAnterior.nodos.length;
    const totalCorteCercano = corteMasCercanoMesAnterior.nodos.length;

    const n2Actual       = totalActual       - (countActual["N0"]       || 0) - (countActual["N1"]       || 0);
    const n2Anterior     = totalAnterior     - (countAnterior["N0"]     || 0) - (countAnterior["N1"]     || 0);
    const n2AnioAnterior = totalAnioAnterior - (countAnioAnterior["N0"] || 0) - (countAnioAnterior["N1"] || 0);
    const n2CorteCercano = totalCorteCercano - (countCorteCercano["N0"] || 0) - (countCorteCercano["N1"] || 0);

    // ── Comparación de dos meses puntuales elegidos libremente (junto a "Fin") ──
    const compA = mesCompA ? filtrarPorMes(mesCompA) : { nodos: [], fechaCorte: null };
    const compB = mesCompB ? filtrarPorMes(mesCompB) : { nodos: [], fechaCorte: null };
    const countCompA = contarPorCat(compA.nodos);
    const countCompB = contarPorCat(compB.nodos);
    const totalCompA = compA.nodos.length;
    const totalCompB = compB.nodos.length;
    const n2CompA = totalCompA - (countCompA["N0"] || 0) - (countCompA["N1"] || 0);
    const n2CompB = totalCompB - (countCompB["N0"] || 0) - (countCompB["N1"] || 0);

    // Solo tiene sentido mostrar la columna/tarjeta de "corte más cercano del mes
    // anterior" cuando ese corte es DISTINTO al último corte del mes anterior —
    // si coinciden (p. ej. el corte actual cae el último día del mes), mostrar
    // las dos columnas es información repetida y confunde la lectura.
    const mostrarCorteCercano = !!(
      corteMasCercanoMesAnterior?.fechaCorte &&
      corteMasCercanoMesAnterior.fechaCorte !== anterior.fechaCorte
    );

    return {
      actual, anterior, anioAnterior, corteMasCercanoMesAnterior, mostrarCorteCercano,
      countActual, countAnterior, countAnioAnterior, countCorteCercano,
      totalActual, totalAnterior, totalAnioAnterior, totalCorteCercano,
      n2Actual, n2Anterior, n2AnioAnterior, n2CorteCercano,
      compA, compB, countCompA, countCompB, totalCompA, totalCompB, n2CompA, n2CompB,
      mesAnteriorLabel: mesAnterior,
      mesAnioAnteriorLabel: mesAnioAnteriorExiste ? mesAnioAnteriorLabel : null,
    };
  })();

  // ── Serie mensual de % de impacto: ofensores N2+ sobre el TOTAL de nodos
  //    evaluados ese mes (incluye N0/N1, el universo completo), tomando el
  //    último corte de cada mes. Usa resumenData (igual que Resumen Disponibilidad,
  //    que sí trae el universo completo) y respeta los filtros activos arriba. ──
  const serieImpactoMensual = (() => {
    if (resumenData.length === 0) return [];
    const coincideFiltros = (r) => {
      const coincideJefe   = filtroJefe === null || (Array.isArray(filtroJefe) && (filtroJefe.length === 0 || filtroJefe.includes((r.nombre_jefe || "").trim())));
      const coincideAliado = filtroAliado === null || (Array.isArray(filtroAliado) && (filtroAliado.length === 0 || filtroAliado.includes((r.aliado || "").trim())));
      const coincideCiudad = filtroCiudad === null || (Array.isArray(filtroCiudad) && (filtroCiudad.length === 0 || filtroCiudad.includes((r.ciudad || "").trim())));
      const pRawR  = (r.tipo_prioridad || r.prioridad || "").toString().toLowerCase();
      const pNormR = pRawR.includes("alto") || pRawR.includes("alta") ? "Alta" : pRawR.includes("medio") || pRawR.includes("media") ? "Media" : "Otra";
      const coincidePrioridad = filtroPrioridad === null || (Array.isArray(filtroPrioridad) && (filtroPrioridad.length === 0 || filtroPrioridad.includes(pNormR)));
      return coincideJefe && coincideAliado && coincideCiudad && coincidePrioridad;
    };
    const porMes = {};
    resumenData.filter(coincideFiltros).forEach(r => {
      const mes = formatearMes(r.m_fecha_mes);
      if (!mes) return;
      const fc = r.fecha_corte.includes("T") ? r.fecha_corte.split("T")[0] : r.fecha_corte;
      if (!porMes[mes]) porMes[mes] = { ultimoCorte: fc, filas: [] };
      if (fc > porMes[mes].ultimoCorte) porMes[mes].ultimoCorte = fc;
      porMes[mes].filas.push({ categoria: (r.categoria || "").trim(), _fc: fc });
    });
    const parseFecha = (str) => { const [mes, anio] = str.split("-").map(Number); return new Date(anio, mes - 1); };
    return Object.entries(porMes).map(([mes, info]) => {
      const nodosDelCorte = info.filas.filter(r => r._fc === info.ultimoCorte);
      const totalNodos = nodosDelCorte.length;
      const n2 = nodosDelCorte.filter(r => r.categoria !== "N0" && r.categoria !== "N1").length;
      const sinImpacto = totalNodos - n2;
      const pctSinImpacto = totalNodos > 0 ? Math.round((sinImpacto / totalNodos) * 10000) / 100 : null;
      return { mes, totalNodos, n2, pctSinImpacto };
    }).sort((a, b) => parseFecha(a.mes) - parseFecha(b.mes));
  })();

  // ── Data para la gráfica "Total ofensores N2+ por mes": combina el conteo
  //    mensual con el % de nodos SIN impacto, la meta corporativa de Disponibilidad
  //    y la línea de tendencia ──
  const dataGraficaTotal = data.map((d, i) => {
    const imp = serieImpactoMensual.find(s => s.mes === d.mes);
    return {
      ...d,
      totalNodos:    imp ? imp.totalNodos   : null,
      pctSinImpacto: imp ? imp.pctSinImpacto : null,
      metaDisponibilidad: METAS_DISPONIBILIDAD[d.mes] ?? null,
      tendencia:     metricsGerenciales ? metricsGerenciales.dataConMovil[i]?.tendencia : null,
    };
  });

  // ── Mide la posición real (en px) de cada mes en el eje X de la gráfica
  //    "Total ofensores N2+ por mes", para que la tabla de resumen de abajo
  //    quede exactamente alineada columna por columna, no por aproximación.
  //    Se reintenta varias veces tras cada cambio de datos porque Recharts
  //    anima la entrada de la gráfica, así que la primera medición puede
  //    tomar una posición todavía no definitiva; solo se acepta una medición
  //    cuyos puntos vengan ordenados y con separación real entre ellos. ──
  useEffect(() => {
    const el = chartTotalWrapRef.current;
    if (!el) return;
    let cancelado = false;
    const timers = [];
    const medir = () => {
      if (cancelado) return;
      const ticks = el.querySelectorAll(".recharts-xAxis .recharts-cartesian-axis-tick");
      const n = dataGraficaTotal.length;
      if (!ticks || ticks.length !== n || n === 0) return;
      const contRect = el.getBoundingClientRect();
      if (contRect.width === 0) return;
      const xs = Array.from(ticks).map(t => {
        const r = t.getBoundingClientRect();
        return (r.left + r.right) / 2 - contRect.left;
      });
      // Validar que las posiciones sean crecientes y con separación real
      // (si no, es una medición tomada a mitad de la animación de entrada)
      let valido = xs.length === 1;
      for (let i = 1; i < xs.length; i++) {
        if (xs[i] - xs[i - 1] > 2) valido = true; else { valido = false; break; }
      }
      if (!valido) return;
      setChartTotalTicksX(xs);
      setChartTotalAncho(contRect.width);
    };
    // Varios intentos espaciados para cubrir el tiempo de animación/layout de Recharts
    [0, 150, 350, 600, 1000].forEach(ms => timers.push(setTimeout(medir, ms)));
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    window.addEventListener("resize", medir);
    return () => { cancelado = true; timers.forEach(clearTimeout); ro.disconnect(); window.removeEventListener("resize", medir); };
  }, [dataGraficaTotal]);

  // ── Piso y techo del eje derecho (%): si el % sin impacto y la meta se
  //    mueven en un rango alto y angosto (ej. 93%–97%), fijar el eje en
  //    0–100 (o incluso en 85–100) aplasta las líneas contra el centro y los
  //    valores quedan casi planos y pegados entre sí. En vez de un piso fijo
  //    cada 5 puntos con techo fijo en 100, se calcula un rango ajustado al
  //    dato real (colchón proporcional a cuánto varían los datos), para que
  //    la gráfica "estire" el espectro y los puntos/etiquetas queden
  //    claramente separados — como en la referencia del panel de GIR. ──
  const valoresPct = dataGraficaTotal
    .flatMap(d => [d.pctSinImpacto, d.metaDisponibilidad])
    .filter(v => v !== null && v !== undefined);
  const [pisoEjeDerecho, techoEjeDerecho] = (() => {
    if (valoresPct.length === 0) return [0, 100];
    const minV = Math.min(...valoresPct);
    const maxV = Math.max(...valoresPct);
    const rango = maxV - minV;
    // Colchón arriba/abajo del dato real: proporcional al rango (para no
    // desperdiciar espacio si ya varía bastante), con un mínimo de 1.5 puntos
    // para que ningún punto quede pegado al borde del eje.
    const colchon = Math.min(4, Math.max(1.5, rango * 0.6));
    let piso = Math.max(0, Math.floor((minV - colchon) * 2) / 2);
    let techo = Math.min(100, Math.ceil((maxV + colchon) * 2) / 2);
    // Separación mínima garantizada (4 puntos) aunque los datos sean casi
    // planos, para que siempre haya "aire" vertical donde dibujar.
    if (techo - piso < 4) {
      const centro = (techo + piso) / 2;
      piso = Math.max(0, centro - 2);
      techo = Math.min(100, centro + 2);
    }
    return [piso, techo];
  })();

  // ── Meses que compara el Semáforo/Ranking de Jefes (vista "mes"): los mismos
  //    que el filtro de arriba usa como mes actual / mes de referencia, para que
  //    toda la página compare siempre el mismo par de meses. ──
  const mesJefeA = mesActivo;
  const mesJefeB = tablaResumen?.mesAnteriorLabel || "";

  const diasMes       = diasDelMes(mesActivo);
  const totalHorasMes = diasMes * 24;
  const totalMinMes   = totalHorasMes * 60;

  // ── Semáforo y ranking por CORTE cercano ─────────────────────────────────
  const semaforoRankingCorte = useMemo(() => {
    if (!tablaResumen || !tablaResumen.corteMasCercanoMesAnterior?.fechaCorte) return null;
    const contarN2PorJefe = (nodos) => {
      const map = {};
      nodos.forEach(n => {
        const jefe = (n.nombre_jefe || "Sin jefe").trim();
        const cat  = (n.categoria   || "").trim();
        if (!map[jefe]) map[jefe] = 0;
        if (cat !== "N0" && cat !== "N1") map[jefe]++;
      });
      return map;
    };
    const actualPorJefe = contarN2PorJefe(tablaResumen.actual.nodos);
    const cercaPorJefe  = contarN2PorJefe(tablaResumen.corteMasCercanoMesAnterior.nodos);
    const todosJefes = [...new Set([...Object.keys(actualPorJefe), ...Object.keys(cercaPorJefe)])].sort();
    const semaforo = todosJefes.map(jefe => {
      const actual   = actualPorJefe[jefe] || 0;
      const anterior = cercaPorJefe[jefe]  || 0;
      const delta    = actual - anterior;
      const pct      = anterior > 0 ? ((delta / anterior) * 100).toFixed(1) : null;
      let estado, color, icon;
      if (delta > 0)      { estado = "Empeoró"; color = C.red;      icon = "🔴"; }
      else if (delta < 0) { estado = "Mejoró";  color = C.greenMid; icon = "🟢"; }
      else                { estado = "Igual";   color = C.amber;    icon = "🟡"; }
      return { jefe, actual, anterior, delta, pct, estado, color, icon };
    });
    const ranking = [...semaforo].sort((a, b) => a.actual - b.actual);
    return {
      semaforo, ranking,
      fechaActual:  formatearFecha(tablaResumen.actual.fechaCorte),
      fechaCercana: formatearFecha(tablaResumen.corteMasCercanoMesAnterior.fechaCorte),
    };
  }, [tablaResumen]);

  // ── Semáforo/Ranking de Jefes comparando los dos meses elegidos por el usuario (vista "mes") ──
  const semaforoJefesSeleccion = useMemo(() => {
    if (!mesJefeA) return [];
    const datosA = jefeData.find(d => d.mes === mesJefeA) || {};
    const datosB = jefeData.find(d => d.mes === mesJefeB) || {};
    const todosJefes = [...new Set([
      ...Object.keys(datosA).filter(k => k !== "mes"),
      ...Object.keys(datosB).filter(k => k !== "mes")
    ])].sort();
    return todosJefes.map(jefe => {
      const actual = datosA[jefe] || 0;
      const anterior = datosB[jefe] || 0;
      const delta = actual - anterior;
      const pct = anterior > 0 ? ((delta / anterior) * 100).toFixed(1) : null;
      let estado, color, icon;
      if (delta > 0)      { estado = "Empeoró"; color = C.red;      icon = "🔴"; }
      else if (delta < 0) { estado = "Mejoró";  color = C.greenMid; icon = "🟢"; }
      else                { estado = "Igual";   color = C.amber;    icon = "🟡"; }
      return { jefe, actual, anterior, delta, pct, estado, color, icon };
    });
  }, [jefeData, mesJefeA, mesJefeB]);

  const rankingJefesSeleccion = useMemo(
    () => [...semaforoJefesSeleccion].sort((a, b) => a.actual - b.actual),
    [semaforoJefesSeleccion]
  );

  const calcularProyeccion = () => {
    if (!tablaResumen) return null;
    const fechaCorte = tablaResumen.actual.fechaCorte;
    if (!fechaCorte) return null;
    const f = fechaCorte.includes("T") ? fechaCorte.split("T")[0] : fechaCorte;
    const diaCorte = parseInt(f.split("-")[2]);
    if (diaCorte <= 0 || diasMes <= 0) return null;
    const proyeccion = Math.round((tablaResumen.n2Actual / diaCorte) * diasMes);
    const variacionProy = proyeccion - tablaResumen.n2Anterior;
    const pctVariacionProy = tablaResumen.n2Anterior > 0
      ? ((variacionProy / tablaResumen.n2Anterior) * 100).toFixed(1) : null;
    return { proyeccion, diaCorte, variacionProy, pctVariacionProy };
  };

  const proyeccionData = calcularProyeccion();

  const cardStyle = {
    marginBottom: "24px",
    background: C.white,
    padding: "20px 24px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)"
  };

  const thStyle = {
    padding: "10px 12px",
    background: C.navy,
    color: "white",
    textAlign: "left",
    fontSize: "12px",
    whiteSpace: "nowrap",
    fontWeight: "600"
  };

  const thClasif = {
    padding: "8px 10px",
    background: C.navy,
    color: "white",
    textAlign: "center",
    fontSize: "11px",
    whiteSpace: "nowrap",
    fontWeight: "500",
    textTransform: "uppercase",
    letterSpacing: "0.05em"
  };

  const axisStyle    = { fill: C.gray, fontWeight: 500, fontSize: 12 };
  const labelStyle   = { fontSize: 11, fontWeight: 600, fill: C.navy };
  const CHART_WIDTH  = 860;
  const CHART_HEIGHT = 360;
  // La gráfica "Total ofensores N2+ por mes" (% sin impacto vs Meta) necesita
  // más alto que las demás para que, con el eje ya ajustado al dato real
  // (ver pisoEjeDerecho/techoEjeDerecho), los puntos y sus etiquetas de %
  // queden bien separados y no se amontonen.
  const CHART_HEIGHT_TOTAL = 460;
  const dotStyle     = { r: 3.5, strokeWidth: 2 };
  const activeDot    = { r: 6, strokeWidth: 0 };

  const labelVar = (val) => val > 0 ? `+${val}` : `${val}`;

  // ── Resumen automático del comportamiento de una gráfica de líneas ──
  // arr: serie de datos [{mes, k1, k2, ...}]; keys: las claves a sumar como "total" de esa gráfica.
  const resumenGrafica = (arr, keys) => {
    if (!arr || arr.length < 2) return null;
    const totales = arr.map(d => keys.reduce((s, k) => s + (Number(d[k]) || 0), 0));
    const reg = regresionLineal(totales);
    const promedio = Math.round(totales.reduce((a, b) => a + b, 0) / totales.length);
    const valMax = Math.max(...totales), valMin = Math.min(...totales);
    const idxMax = totales.indexOf(valMax), idxMin = totales.indexOf(valMin);
    const ultimo   = totales[totales.length - 1];
    const anterior = totales.length > 1 ? totales[totales.length - 2] : null;
    const delta = anterior !== null ? ultimo - anterior : null;
    const tendencia = reg ? reg.pendiente : 0;
    const tendenciaLabel = tendencia > 0.5 ? "Al alza" : tendencia < -0.5 ? "A la baja" : "Estable";
    const tendenciaColor = tendencia > 0.5 ? C.red : tendencia < -0.5 ? C.greenMid : C.amber;
    const tendenciaIcon  = tendencia > 0.5 ? "📈" : tendencia < -0.5 ? "📉" : "➡️";

    let dominante = null;
    if (keys.length > 1) {
      const ultimoRow = arr[arr.length - 1];
      const vals = keys.map(k => ({ key: k, valor: Number(ultimoRow[k]) || 0 })).sort((a, b) => b.valor - a.valor);
      const top = vals[0];
      if (top && top.valor > 0) {
        dominante = { key: top.key, valor: top.valor, pct: ultimo > 0 ? Math.round((top.valor / ultimo) * 100) : 0 };
      }
    }

    return {
      mesUlt: arr[arr.length - 1]?.mes, mesAnt: arr[arr.length - 2]?.mes,
      ultimo, anterior, delta, promedio,
      mesMax: arr[idxMax]?.mes, valMax,
      mesMin: arr[idxMin]?.mes, valMin,
      tendenciaLabel, tendenciaColor, tendenciaIcon,
      dominante,
    };
  };

  // ── Franja de resumen debajo del título de cada gráfica ──
  const ResumenGrafica = ({ r, labelDominante }) => {
    if (!r) return null;
    const Sep = () => <span style={{ width: "1px", height: "13px", background: "#e2e8f0" }} />;
    return (
      <div style={{
        display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center",
        background: "#f8fafc", border: "1px solid #eef2f6", borderRadius: "9px",
        padding: "9px 14px", marginBottom: "14px", fontSize: "12px", color: C.slate,
      }}>
        <span style={{ display: "flex", alignItems: "center", gap: "5px", fontWeight: "700", color: r.tendenciaColor }}>
          {r.tendenciaIcon} {r.tendenciaLabel}
        </span>
        <Sep />
        <span>Promedio: <b style={{ color: C.navy }}>{r.promedio}</b>/mes</span>
        <Sep />
        <span>Pico: <b style={{ color: C.navy }}>{r.mesMax}</b> ({r.valMax})</span>
        <Sep />
        <span>Mínimo: <b style={{ color: C.navy }}>{r.mesMin}</b> ({r.valMin})</span>
        <Sep />
        <span>
          Último ({r.mesUlt}): <b style={{ color: C.navy }}>{r.ultimo}</b>
          {r.delta !== null && (
            <span style={{ color: r.delta > 0 ? C.red : r.delta < 0 ? C.greenMid : C.slate, fontWeight: "700", marginLeft: "4px" }}>
              {r.delta > 0 ? `+${r.delta}` : r.delta}
            </span>
          )}
        </span>
        {r.dominante && (
          <>
            <Sep />
            <span>{labelDominante || "Mayor peso"}: <b style={{ color: C.navy }}>{r.dominante.key}</b> ({r.dominante.pct}%)</span>
          </>
        )}
      </div>
    );
  };

  // ── Bloque KPI reutilizable — tarjeta vertical, pensada para apilarse en una columna ──
  const KPIBloque = ({ titulo, n2Ref, n2Actual, fechaRef, fechaActual, labelVs }) => {
    const variacion = n2Actual - n2Ref;
    const pct = n2Ref > 0 ? ((variacion / n2Ref) * 100).toFixed(1) : null;
    const esMejora = variacion < 0;
    const mostrarProyeccion = proyeccionData && labelVs === "mes ant.";
    return (
      <div style={{
        background: C.white,
        borderRadius: "14px",
        padding: "18px 22px",
        boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
        border: "1px solid #e2e8f0",
        borderLeft: `4px solid ${C.red}`,
      }}>
        <div style={{ fontSize: "10px", color: C.slate, fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.09em", marginBottom: "14px" }}>
          {titulo}
        </div>
        <div style={{ display: "flex", gap: "14px", alignItems: "flex-end", flexWrap: "wrap", justifyContent: "space-between" }}>
          <div style={{ display: "flex", gap: "14px", alignItems: "flex-end" }}>
            {n2Ref !== null && (
              <div>
                <div style={{ fontSize: "10px", color: C.light, marginBottom: "2px", fontWeight: "500" }}>{fechaRef}</div>
                <div style={{ fontSize: "24px", fontWeight: "800", color: "#334155", lineHeight: 1 }}>{n2Ref}</div>
              </div>
            )}
            {n2Ref !== null && (
              <div style={{ color: "#cbd5e1", fontSize: "16px", marginBottom: "4px" }}>→</div>
            )}
            <div>
              <div style={{ fontSize: "10px", color: C.slate, marginBottom: "2px", fontWeight: "500" }}>{fechaActual}</div>
              <div style={{ fontSize: "30px", fontWeight: "800", color: C.navy, lineHeight: 1 }}>{n2Actual}</div>
            </div>
          </div>
          {n2Ref !== null && (
            <div style={{
              background: esMejora ? "rgba(22,163,74,0.08)" : "rgba(227,6,19,0.08)",
              border: `1px solid ${esMejora ? "rgba(22,163,74,0.3)" : "rgba(227,6,19,0.3)"}`,
              borderRadius: "8px",
              padding: "6px 12px",
              textAlign: "center",
            }}>
              <div style={{ fontSize: "10px", color: C.slate, marginBottom: "2px" }}>Variación</div>
              <div style={{ fontSize: "16px", fontWeight: "700", color: esMejora ? C.green : C.red, lineHeight: 1 }}>
                {labelVar(variacion)}
              </div>
              {pct && (
                <div style={{ fontSize: "10px", color: esMejora ? C.greenMid : C.red, marginTop: "2px" }}>
                  {pct}%
                </div>
              )}
            </div>
          )}
        </div>

        {mostrarProyeccion && (() => {
          const vp = proyeccionData.variacionProy;
          const esMejoraProy = vp < 0;
          return (
            <div style={{ marginTop: "14px", paddingTop: "14px", borderTop: "1px dashed #e2e8f0" }}>
              <div style={{ fontSize: "10px", color: C.slate, fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.09em", marginBottom: "10px" }}>
                🔮 Proyección fin de mes
              </div>
              <div style={{ display: "flex", gap: "14px", alignItems: "flex-end", flexWrap: "wrap", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontSize: "10px", color: C.light, marginBottom: "2px" }}>día {proyeccionData.diaCorte} de {diasMes}</div>
                  <div style={{ fontSize: "24px", fontWeight: "800", color: esMejoraProy ? C.green : C.red, lineHeight: 1 }}>{proyeccionData.proyeccion}</div>
                </div>
                {tablaResumen.mesAnteriorLabel && (
                  <div style={{
                    background: esMejoraProy ? "rgba(22,163,74,0.08)" : "rgba(227,6,19,0.08)",
                    border: `1px solid ${esMejoraProy ? "rgba(22,163,74,0.3)" : "rgba(227,6,19,0.3)"}`,
                    borderRadius: "8px",
                    padding: "6px 12px",
                    textAlign: "center",
                  }}>
                    <div style={{ fontSize: "10px", color: C.slate, marginBottom: "2px" }}>vs {labelVs}</div>
                    <div style={{ fontSize: "15px", fontWeight: "700", color: esMejoraProy ? C.green : C.red, lineHeight: 1 }}>
                      {labelVar(vp)}
                    </div>
                    {proyeccionData.pctVariacionProy && (
                      <div style={{ fontSize: "10px", color: esMejoraProy ? C.greenMid : C.red, marginTop: "2px" }}>
                        {proyeccionData.pctVariacionProy}%
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  // ── KPI Card gerencial ──
  const KPICard = ({ icon, label, value, sub, color }) => (
    <div style={{
      background: C.white,
      borderRadius: "12px",
      padding: "14px 16px",
      boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
      border: "1px solid #e2e8f0",
      display: "flex",
      flexDirection: "column",
      gap: "10px",
      height: "100%",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "9px" }}>
        <div style={{
          width: "30px", height: "30px", borderRadius: "8px", flexShrink: 0,
          background: `${color || C.red}1a`, display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "14px",
        }}>{icon}</div>
        <div style={{ fontSize: "10px", color: C.slate, textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: "700" }}>{label}</div>
      </div>
      <div>
        <div style={{ fontSize: "23px", fontWeight: "800", color: color || C.navy, lineHeight: 1 }}>{value}</div>
        {sub && <div style={{ fontSize: "11px", color: C.light, marginTop: "4px" }}>{sub}</div>}
      </div>
    </div>
  );

  // ── Tooltip personalizado para gráficas ──
  const CustomTooltipN2 = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <div style={{ background: C.white, border: "1px solid #e2e8f0", borderTop: `3px solid ${C.red}`, borderRadius: "10px", padding: "12px 16px", color: C.navy, fontSize: "12px", minWidth: "180px", boxShadow: "0 4px 16px rgba(0,0,0,0.10)" }}>
        <div style={{ fontWeight: "700", marginBottom: "8px", color: C.slate }}>{label}</div>
        {payload.map((p, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: "12px", marginBottom: "4px" }}>
            <span style={{ color: p.color }}>{p.name}</span>
            <span style={{ fontWeight: "700", color: C.navy }}>{p.value !== null && p.value !== undefined ? p.value : "—"}</span>
          </div>
        ))}
      </div>
    );
  };

  const CustomTooltipTotalN2 = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const row = payload[0].payload;
    return (
      <div style={{ background: C.white, border: "1px solid #e2e8f0", borderTop: `3px solid ${C.red}`, borderRadius: "10px", padding: "12px 16px", color: C.navy, fontSize: "12px", minWidth: "200px", boxShadow: "0 4px 16px rgba(0,0,0,0.10)" }}>
        <div style={{ fontWeight: "700", marginBottom: "8px", color: C.slate }}>{label}</div>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", marginBottom: "4px" }}>
          <span style={{ color: C.silverDark }}>Ofensores N2+</span>
          <span style={{ fontWeight: "700", color: C.navy }}>{row.total}</span>
        </div>
        {row.totalNodos !== null && row.totalNodos !== undefined && (
          <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", marginBottom: "4px" }}>
            <span style={{ color: C.slate }}>Total nodos evaluados</span>
            <span style={{ fontWeight: "700", color: C.navy }}>{row.totalNodos}</span>
          </div>
        )}
        {row.pctSinImpacto !== null && row.pctSinImpacto !== undefined && (
          <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", marginBottom: "4px" }}>
            <span style={{ color: C.red }}>% de nodos sin impacto</span>
            <span style={{ fontWeight: "700", color: C.navy }}>{row.pctSinImpacto.toFixed(2)}%</span>
          </div>
        )}
        {row.metaDisponibilidad !== null && row.metaDisponibilidad !== undefined && (
          <div style={{ display: "flex", justifyContent: "space-between", gap: "12px" }}>
            <span style={{ color: C.redWine }}>Meta Disponibilidad</span>
            <span style={{ fontWeight: "700", color: C.navy }}>{row.metaDisponibilidad.toFixed(2)}%</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: "4px 0" }}>

      <div style={{ marginBottom: "22px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px" }}>
          <div style={{ width: "4px", height: "24px", background: C.red, borderRadius: "2px" }} />
          <h2 style={{ margin: 0, color: C.navy, fontSize: "20px", fontWeight: "700" }}>Dashboard General</h2>
          {soloLectura && <BannerSoloLectura />}
        </div>
        <p style={{ margin: "0 0 0 14px", fontSize: "13px", color: C.slate }}>
          Vista gerencial de ofensores N2+: tendencia, disponibilidad y comparativo por jefe de zona
        </p>
      </div>

      {/* ══ FILTROS GENERALES (aplican a todas las tablas y gráficas de esta página) ══ */}
      <div style={{ ...cardStyle, marginBottom: "22px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px", flexWrap: "wrap", gap: "12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div style={{ width: "3px", height: "18px", background: C.red, borderRadius: "2px" }} />
            <h3 style={{ margin: 0, color: C.navy, fontSize: "15px", fontWeight: "600" }}>Filtros</h3>
          </div>
          <div style={{ display: "flex", gap: "6px" }}>
            {["rango", "comparar"].map(m => (
              <button key={m} onClick={() => setModoFiltroFecha(m)} style={{
                padding: "5px 12px", borderRadius: "20px", fontSize: "11px", fontWeight: "600", cursor: "pointer",
                background: modoFiltroFecha === m ? C.navy : "#f1f5f9",
                color: modoFiltroFecha === m ? "#fff" : C.slate,
                border: modoFiltroFecha === m ? `1px solid ${C.navy}` : "1px solid #e2e8f0"
              }}>
                {m === "rango" ? "📅 Inicio/Fin" : "🔀 Comparar"}
              </button>
            ))}
          </div>
        </div>

        {/* ── Barra de filtros ── */}
        <div style={{
          display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap",
          background: "#f8fafc", border: "1px solid #eef2f6", borderRadius: "10px",
          padding: "10px 14px",
        }}>
          <label style={{ fontWeight: "500", fontSize: "12px", color: modoFiltroFecha === "rango" ? C.slate : "#c2c9d3" }}>Inicio:</label>
          <select value={mesDesdeFiltro} onChange={e => setMesDesdeFiltro(e.target.value)}
            disabled={modoFiltroFecha !== "rango"}
            style={{ padding: "6px 10px", borderRadius: "6px", border: "1px solid #e2e8f0", fontSize: "12px",
              color: modoFiltroFecha === "rango" ? "#1a2b4a" : "#b6bdc7",
              background: modoFiltroFecha === "rango" ? "white" : "#f1f5f9",
              cursor: modoFiltroFecha === "rango" ? "pointer" : "not-allowed", opacity: modoFiltroFecha === "rango" ? 1 : 0.6 }}>
            <option value="">(Todos)</option>
            {mesesDisponibles.map((m, i) => <option key={i} value={m}>{m}</option>)}
          </select>
          <label style={{ fontWeight: "500", fontSize: "12px", color: modoFiltroFecha === "rango" ? C.slate : "#c2c9d3" }}>Fin:</label>
          <select value={mesSeleccionado} onChange={e => setMesSeleccionado(e.target.value)}
            disabled={modoFiltroFecha !== "rango"}
            style={{ padding: "6px 10px", borderRadius: "6px", border: "1px solid #e2e8f0", fontSize: "12px",
              color: modoFiltroFecha === "rango" ? "#1a2b4a" : "#b6bdc7",
              background: modoFiltroFecha === "rango" ? "white" : "#f1f5f9",
              cursor: modoFiltroFecha === "rango" ? "pointer" : "not-allowed", opacity: modoFiltroFecha === "rango" ? 1 : 0.6 }}>
            {mesesDisponibles.map((m, i) => <option key={i} value={m}>{m}</option>)}
          </select>

          <label style={{ fontWeight: "500", fontSize: "12px", color: modoFiltroFecha === "comparar" ? C.slate : "#c2c9d3" }}>Comparar:</label>
          <select value={mesCompA} onChange={e => setMesCompA(e.target.value)}
            disabled={modoFiltroFecha !== "comparar"}
            style={{ padding: "6px 10px", borderRadius: "6px", border: "1px solid #e2e8f0", fontSize: "12px",
              color: modoFiltroFecha === "comparar" ? "#1a2b4a" : "#b6bdc7",
              background: modoFiltroFecha === "comparar" ? "white" : "#f1f5f9",
              cursor: modoFiltroFecha === "comparar" ? "pointer" : "not-allowed", opacity: modoFiltroFecha === "comparar" ? 1 : 0.6 }}>
            {mesesDisponibles.map((m, i) => <option key={i} value={m}>{m}</option>)}
          </select>
          <span style={{ fontSize: "12px", color: modoFiltroFecha === "comparar" ? C.slate : "#c2c9d3" }}>vs</span>
          <select value={mesCompB} onChange={e => setMesCompB(e.target.value)}
            disabled={modoFiltroFecha !== "comparar"}
            style={{ padding: "6px 10px", borderRadius: "6px", border: "1px solid #e2e8f0", fontSize: "12px",
              color: modoFiltroFecha === "comparar" ? "#1a2b4a" : "#b6bdc7",
              background: modoFiltroFecha === "comparar" ? "white" : "#f1f5f9",
              cursor: modoFiltroFecha === "comparar" ? "pointer" : "not-allowed", opacity: modoFiltroFecha === "comparar" ? 1 : 0.6 }}>
            {mesesDisponibles.map((m, i) => <option key={i} value={m}>{m}</option>)}
          </select>

          <div style={{ width: "1px", height: "22px", background: "#e2e8f0", margin: "0 2px" }} />

          <DropdownMulti
            label="Jefe Zona:"
            opciones={optsJefe}
            valor={filtroJefe}
            onChange={v => setFiltroJefe(v)}
            placeholder="Todos"
          />
          <DropdownMulti
            label="Aliado:"
            opciones={optsAliado}
            valor={filtroAliado}
            onChange={v => setFiltroAliado(v)}
            placeholder="Todos"
          />
          <DropdownMulti
            label="Ciudad:"
            opciones={optsCiudad}
            valor={filtroCiudad}
            onChange={v => setFiltroCiudad(v)}
            placeholder="Todas"
          />
          <DropdownMulti
            label="Prioridad:"
            opciones={optsPrioridad}
            valor={filtroPrioridad}
            onChange={v => setFiltroPrioridad(v)}
            placeholder="Todas"
          />
          {(mesDesdeFiltro !== "" || filtroJefe !== null || filtroAliado !== null || filtroCiudad !== null || filtroPrioridad !== null) && (
            <button onClick={() => { setMesDesdeFiltro(""); setFiltroJefe(null); setFiltroAliado(null); setFiltroCiudad(null); setFiltroPrioridad(null); }}
              style={{ padding: "5px 10px", borderRadius: "6px", border: "1px solid #e2e8f0", background: "white", color: "#64748b", fontSize: "11px", cursor: "pointer", fontWeight: "600", marginLeft: "auto" }}>
              ✕ Limpiar
            </button>
          )}
        </div>
        <p style={{ margin: "10px 0 0", fontSize: "11px", color: C.light }}>
          Estos filtros aplican a todas las tablas y gráficas de este panel.
        </p>
      </div>

      {/* ══ BLOQUE 1: KPIs Gerenciales + Gráfica Total N2+ (ancho completo) ══ */}
      <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginBottom: "28px" }}>

      {/* ══ KPIs GERENCIALES ══ */}
      {metricsGerenciales && (
        <div style={{ marginBottom: "24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
            <div style={{ width: "3px", height: "16px", background: C.red, borderRadius: "2px" }} />
            <h3 style={{ margin: 0, color: C.navy, fontSize: "14px", fontWeight: "600" }}>Indicadores Gerenciales</h3>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "12px" }}>
            <KPICard
              icon="📈"
              label="Tendencia N2+"
              value={metricsGerenciales.labelTendencia}
              sub={`${metricsGerenciales.tendencia > 0 ? "+" : ""}${metricsGerenciales.tendencia.toFixed(1)} ofensores/mes`}
              color={metricsGerenciales.colorTendencia}
            />
            <KPICard
              icon="📊"
              label="Promedio histórico"
              value={metricsGerenciales.promedio}
              sub="ofensores N2+ por mes"
              color="#60a5fa"
            />
            <KPICard
              icon="🏆"
              label="Mejor mes"
              value={metricsGerenciales.min}
              sub={metricsGerenciales.mejorMes?.mes || ""}
              color={C.greenMid}
            />
            <KPICard
              icon="⚠️"
              label="Peor mes"
              value={metricsGerenciales.max}
              sub={metricsGerenciales.peorMes?.mes || ""}
              color={C.red}
            />
            <KPICard
              icon="📅"
              label="Meses analizados"
              value={metricsGerenciales.mesesAnalizados}
              sub="cortes históricos"
              color="#a78bfa"
            />
            {metricsGerenciales.mayorCargaJefe && (
              <KPICard
                icon="👤"
                label="Mayor carga actual"
                value={metricsGerenciales.mayorCargaVal}
                sub={metricsGerenciales.mayorCargaJefe}
                color={C.orange}
              />
            )}
          </div>
        </div>
      )}

      {/* ══ GRÁFICA TOTAL N2+ POR MES ══ */}
      <div style={cardStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
          <div style={{ width: "3px", height: "16px", background: "#10b981", borderRadius: "2px" }} />
          <h3 style={{ margin: 0, color: C.navy, fontSize: "14px", fontWeight: "600" }}>Total ofensores N2+ por mes</h3>
        </div>
        {(() => {
          const statsOfensores = resumenGrafica(data, ["total"]);
          return <ResumenGrafica r={statsOfensores} />;
        })()}

        <div style={{
          background: C.white,
          border: "1px solid #eef2f6",
          borderRadius: "10px 10px 0 0",
          padding: "10px 0 6px 0",
        }}>
          <div ref={chartTotalWrapRef}>
          <ResponsiveContainer width="100%" height={CHART_HEIGHT_TOTAL}>
          <ComposedChart data={dataGraficaTotal} margin={{ top: 28, right: 24, left: 114, bottom: 10 }}>
            <CartesianGrid strokeDasharray="2 4" stroke="#eef2f6" vertical={false} />
            <XAxis dataKey="mes" tick={axisStyle} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
            <YAxis tick={axisStyle} axisLine={false} tickLine={false} width={44}
              domain={[pisoEjeDerecho, techoEjeDerecho]} tickFormatter={(v) => `${v}%`} />
            <Tooltip content={<CustomTooltipTotalN2 />} />
            <Legend wrapperStyle={{ color: C.navy, fontWeight: "500", fontSize: "13px" }} iconType="circle" />
            <Line type="monotone" dataKey="pctSinImpacto" name="% sin impacto" stroke={C.red} strokeWidth={2.6}
              isAnimationActive={false}
              dot={{ r: 3.5, strokeWidth: 2, stroke: C.red, fill: C.white }} activeDot={{ r: 6, strokeWidth: 0 }}>
              <LabelList dataKey="pctSinImpacto" position="top" offset={14} formatter={(v) => (v !== null && v !== undefined ? `${v.toFixed(2)}%` : "")}
                style={{ fontSize: 11, fontWeight: 700, fill: C.red }} />
            </Line>
            <Line type="monotone" dataKey="metaDisponibilidad" name="Meta Disponibilidad" stroke={C.redWine}
              strokeWidth={2} isAnimationActive={false} dot={{ r: 3, strokeWidth: 1.5, stroke: C.redWine, fill: C.white }}
              activeDot={{ r: 5, strokeWidth: 0 }} connectNulls>
              <LabelList dataKey="metaDisponibilidad" position="bottom" offset={14} formatter={(v) => (v !== null && v !== undefined ? `${v.toFixed(2)}%` : "")}
                style={{ fontSize: 10, fontWeight: 600, fill: C.redWine }} />
            </Line>
          </ComposedChart>
          </ResponsiveContainer>
          </div>
        </div>

        {/* ── Mini-panel: cantidad de Ofensores N2+ (línea + área), debajo de
               la gráfica principal, integrado visualmente como parte de la
               misma tarjeta (mismo borde, sin costura) — mismo gutter
               izquierdo/derecho que la gráfica de arriba y la tabla de abajo,
               para que los 3 compartan exactamente las mismas columnas ── */}
        {(() => {
          const st = resumenGrafica(data, ["total"]);
          if (!st) return null;
          const sube = st.delta !== null && st.delta > 0;
          const baja = st.delta !== null && st.delta < 0;
          return (
            <div style={{
              background: "linear-gradient(180deg, #fbfcfd 0%, #f8fafc 100%)",
              border: "1px solid #eef2f6",
              borderTop: "none",
              borderRadius: "0 0 10px 10px",
              padding: "8px 0 8px 0",
            }}>
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "baseline",
                marginLeft: "114px", marginRight: "24px", marginBottom: "2px",
              }}>
                <span style={{ fontSize: "10px", fontWeight: "700", color: C.slate, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Cantidad de ofensores N2+
                </span>
                <span style={{ fontSize: "11px", color: C.slate }}>
                  Último ({st.mesUlt}): <b style={{ color: C.navy, fontSize: "13px" }}>{st.ultimo}</b>
                  {st.delta !== null && (
                    <span style={{ color: sube ? C.red : baja ? C.greenMid : C.slate, fontWeight: "700", marginLeft: "5px" }}>
                      {st.delta > 0 ? `+${st.delta}` : st.delta}
                    </span>
                  )}
                </span>
              </div>
              <ResponsiveContainer width="100%" height={110}>
                <AreaChart data={dataGraficaTotal} margin={{ top: 20, right: 24, left: 114, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradOfensoresMini" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"  stopColor={C.silverDark} stopOpacity={0.32} />
                      <stop offset="100%" stopColor={C.silverDark} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="2 4" stroke="#eef2f6" vertical={false} />
                  <XAxis dataKey="mes" hide />
                  <YAxis tick={{ ...axisStyle, fontSize: 10 }} axisLine={false} tickLine={false} width={44} allowDecimals={false} />
                  <Tooltip content={<CustomTooltipTotalN2 />} />
                  <Area type="monotone" dataKey="total" name="Ofensores N2+" stroke={C.silverDark} strokeWidth={2.2}
                    fill="url(#gradOfensoresMini)" isAnimationActive={false}
                    dot={{ r: 2.8, strokeWidth: 2, stroke: C.silverDark, fill: C.white }} activeDot={{ r: 5, strokeWidth: 0 }}>
                    <LabelList dataKey="total" position="top" offset={8}
                      style={{ fontSize: 10, fontWeight: 700, fill: C.silverDark }} />
                  </Area>
                </AreaChart>
              </ResponsiveContainer>
            </div>
          );
        })()}

        {/* ── Resumen por mes, alineado pixel a pixel con los meses de la gráfica
               (medido en vivo, igual que en Priorización Huella) ── */}
        {(() => {
          const n = dataGraficaTotal.length;
          const medido = chartTotalTicksX.length === n && chartTotalAncho > 0;
          let gridTemplateColumns;
          if (medido) {
            // Franja de etiqueta: desde el borde izquierdo hasta el primer mes.
            // Cada columna de mes: del punto medio con el mes anterior al punto
            // medio con el siguiente (el primero y el último llegan hasta los
            // bordes real del área de la gráfica).
            const bounds = [0, chartTotalTicksX[0]];
            for (let i = 0; i < n - 1; i++) bounds.push((chartTotalTicksX[i] + chartTotalTicksX[i + 1]) / 2);
            bounds.push(chartTotalAncho);
            const widths = [];
            for (let i = 0; i < bounds.length - 1; i++) widths.push(Math.max(0, bounds[i + 1] - bounds[i]));
            gridTemplateColumns = widths.map(w => `${w}px`).join(" ");
          } else {
            // Mientras se mide (primer render), aproximación razonable
            gridTemplateColumns = `158px repeat(${n}, 1fr)`;
          }
          const celdaBase = { padding: "7px 6px", fontSize: "12px", fontWeight: "600", borderBottom: "1px solid #f1f5f9", textAlign: "center" };
          const cabBase   = { padding: "7px 6px", fontSize: "11px", fontWeight: "700", color: C.slate, borderBottom: "2px solid #e2e8f0", textAlign: "center" };
          const filas = [
            {
              label: "Ofensores N2+", color: C.silverDark,
              valor: (d) => d.total,
            },
            {
              label: "% sin impacto",
              valor: (d) => (d.pctSinImpacto !== null && d.pctSinImpacto !== undefined ? `${d.pctSinImpacto.toFixed(2)}%` : "—"),
              color: (d) => (d.pctSinImpacto === null || d.pctSinImpacto === undefined) ? C.slate
                : (d.metaDisponibilidad == null || d.pctSinImpacto >= d.metaDisponibilidad) ? C.green : C.red,
            },
            {
              label: "Meta Disponibilidad", color: C.redWine,
              valor: (d) => (d.metaDisponibilidad !== null && d.metaDisponibilidad !== undefined ? `${d.metaDisponibilidad.toFixed(2)}%` : "—"),
            },
            {
              label: "Diferencia",
              valor: (d) => (d.pctSinImpacto != null && d.metaDisponibilidad != null)
                ? `${(d.pctSinImpacto - d.metaDisponibilidad) >= 0 ? "+" : ""}${(d.pctSinImpacto - d.metaDisponibilidad).toFixed(2)}%`
                : "—",
              color: (d) => (d.pctSinImpacto != null && d.metaDisponibilidad != null)
                ? ((d.pctSinImpacto - d.metaDisponibilidad) >= 0 ? C.green : C.red) : C.slate,
            },
          ];
          return (
            <div style={{ marginTop: "16px" }}>
              <div style={{ fontSize: "10px", fontWeight: "700", color: C.slate, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "8px" }}>
                Resumen por mes — alineado con los meses de la gráfica
              </div>
              <div style={{ display: "grid", gridTemplateColumns, fontSize: "12px" }}>
                <div style={{ ...cabBase, background: "#f8fafc" }} />
                {dataGraficaTotal.map((d, i) => (
                  <div key={d.mes} style={{ ...cabBase, background: "#f8fafc" }}>{d.mes}</div>
                ))}

                {filas.map((fila, fi) => {
                  const bgFila = fi % 2 === 0 ? "white" : "#f9fafb";
                  return (
                    <div key={fila.label} style={{ display: "contents" }}>
                      <div style={{ ...celdaBase, textAlign: "left", fontWeight: "700", color: C.navy, background: "#f8fafc", whiteSpace: "nowrap" }}>
                        {fila.label}
                      </div>
                      {dataGraficaTotal.map((d) => (
                        <div key={d.mes} style={{ ...celdaBase, background: bgFila, color: typeof fila.color === "function" ? fila.color(d) : fila.color }}>
                          {fila.valor(d)}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>

      </div>

      {/* ══ BLOQUE 2: Resumen Disponibilidad (ancho completo, debajo del bloque anterior) ══ */}
      <div style={cardStyle}>

        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
          <div style={{ width: "3px", height: "18px", background: C.red, borderRadius: "2px" }} />
          <h3 style={{ margin: 0, color: C.navy, fontSize: "15px", fontWeight: "600" }}>Resumen Disponibilidad</h3>
        </div>

        {tablaResumen ? (
          <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", alignItems: "flex-start" }}>
            <div style={{ flex: "0 1 380px", minWidth: "300px", display: "flex", flexDirection: "column", gap: "14px" }}>
              {tablaResumen.mesAnteriorLabel && (
                <KPIBloque
                  titulo={modoFiltroFecha === "comparar" ? `Ofensores N2+ — ${mesCompA} vs ${mesCompB}` : "Ofensores N2+"}
                  n2Ref={tablaResumen.n2Anterior}
                  n2Actual={tablaResumen.n2Actual}
                  fechaRef={formatearFecha(tablaResumen.anterior.fechaCorte)}
                  fechaActual={formatearFecha(tablaResumen.actual.fechaCorte)}
                  labelVs={modoFiltroFecha === "comparar" ? "comparación" : "mes ant."}
                />
              )}
              {tablaResumen.corteMasCercanoMesAnterior?.fechaCorte && (
                <KPIBloque
                  titulo={`Ofensores N2+ — Corte vs ${formatearFecha(tablaResumen.corteMasCercanoMesAnterior.fechaCorte)}`}
                  n2Ref={tablaResumen.n2CorteCercano}
                  n2Actual={tablaResumen.n2Actual}
                  fechaRef={formatearFecha(tablaResumen.corteMasCercanoMesAnterior.fechaCorte)}
                  fechaActual={formatearFecha(tablaResumen.actual.fechaCorte)}
                  labelVs="corte ant."
                />
              )}
              {tablaResumen.mesAnioAnteriorLabel ? (
                <KPIBloque
                  titulo={`Ofensores N2+ vs ${tablaResumen.mesAnioAnteriorLabel}`}
                  n2Ref={tablaResumen.n2AnioAnterior}
                  n2Actual={tablaResumen.n2Actual}
                  fechaRef={formatearFecha(tablaResumen.anioAnterior.fechaCorte)}
                  fechaActual={formatearFecha(tablaResumen.actual.fechaCorte)}
                  labelVs="año ant."
                />
              ) : (
                <div style={{
                  background: "#f8fafc", borderRadius: "10px", padding: "14px 20px",
                  border: "1px solid #e2e8f0", display: "flex", alignItems: "center",
                  gap: "10px", color: C.slate, fontSize: "13px", height: "100%", boxSizing: "border-box"
                }}>
                  <span style={{ fontSize: "18px" }}>📅</span>
                  <div>
                    <div style={{ fontWeight: "600", color: C.navy, marginBottom: "2px" }}>
                      vs {mismoMesAnioAnterior(mesActivo)}
                    </div>
                    <div style={{ fontSize: "12px" }}>Sin datos del año anterior</div>
                  </div>
                </div>
              )}
            </div>

            <div style={{ flex: "1 1 480px", minWidth: "320px", display: "flex", flexDirection: "column", gap: "20px" }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Clasificación</th>
                      {tablaResumen.mesAnteriorLabel && (
                        <>
                          <th style={{ ...thStyle, textAlign: "center" }}>{formatearFecha(tablaResumen.anterior.fechaCorte)}</th>
                          <th style={{ ...thStyle, textAlign: "center" }}>%</th>
                        </>
                      )}
                      {tablaResumen.mostrarCorteCercano && (
                        <>
                          <th style={{ ...thStyle, textAlign: "center", background: "#1e3a5f" }}>{formatearFecha(tablaResumen.corteMasCercanoMesAnterior.fechaCorte)}</th>
                          <th style={{ ...thStyle, textAlign: "center", background: "#1e3a5f" }}>%</th>
                        </>
                      )}
                      <th style={{ ...thStyle, textAlign: "center" }}>{formatearFecha(tablaResumen.actual.fechaCorte)}</th>
                      <th style={{ ...thStyle, textAlign: "center" }}>%</th>
                      {tablaResumen.mesAnteriorLabel && (
                        <th style={{ ...thStyle, textAlign: "center" }}>Variación</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {CLASIFICACIONES.map((clas) => {
                      const cA    = tablaResumen.countActual[clas.cat]       || 0;
                      const cAnt  = tablaResumen.countAnterior[clas.cat]     || 0;
                      const cCerc = tablaResumen.countCorteCercano[clas.cat] || 0;
                      const pA    = tablaResumen.totalActual       > 0 ? ((cA    / tablaResumen.totalActual)       * 100).toFixed(2) : "0.00";
                      const pAnt  = tablaResumen.totalAnterior     > 0 ? ((cAnt  / tablaResumen.totalAnterior)     * 100).toFixed(2) : "0.00";
                      const pCerc = tablaResumen.totalCorteCercano > 0 ? ((cCerc / tablaResumen.totalCorteCercano) * 100).toFixed(2) : "0.00";
                      const v     = cA - cAnt;
                      return (
                        <tr key={clas.cat} style={{ background: clas.color }}>
                          <td style={{ padding: "8px 12px", color: "#fff", fontWeight: "700" }}>{clas.label}</td>
                          {tablaResumen.mesAnteriorLabel && (
                            <>
                              <td style={{ padding: "8px 12px", textAlign: "center", color: "#fff", fontWeight: "700" }}>{cAnt || ""}</td>
                              <td style={{ padding: "8px 12px", textAlign: "center", color: "rgba(255,255,255,0.8)" }}>{cAnt > 0 ? `${pAnt}%` : ""}</td>
                            </>
                          )}
                          {tablaResumen.mostrarCorteCercano && (
                            <>
                              <td style={{ padding: "8px 12px", textAlign: "center", color: "#fff", fontWeight: "700", opacity: 0.85 }}>{cCerc || ""}</td>
                              <td style={{ padding: "8px 12px", textAlign: "center", color: "rgba(255,255,255,0.7)" }}>{cCerc > 0 ? `${pCerc}%` : ""}</td>
                            </>
                          )}
                          <td style={{ padding: "8px 12px", textAlign: "center", color: "#fff", fontWeight: "700" }}>{cA || ""}</td>
                          <td style={{ padding: "8px 12px", textAlign: "center", color: "rgba(255,255,255,0.8)" }}>{cA > 0 ? `${pA}%` : ""}</td>
                          {tablaResumen.mesAnteriorLabel && (
                            <td style={{ padding: "8px 12px", textAlign: "center", color: "#fff", fontWeight: "700" }}>
                              {v !== 0 ? (v > 0 ? `+${v}` : v) : ""}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                    <tr style={{ background: C.navy }}>
                      <td style={{ padding: "8px 12px", color: "white", fontWeight: "700" }}>T O T A L</td>
                      {tablaResumen.mesAnteriorLabel && (
                        <>
                          <td style={{ padding: "8px 12px", textAlign: "center", color: "white", fontWeight: "700" }}>{tablaResumen.totalAnterior}</td>
                          <td style={{ padding: "8px 12px", textAlign: "center", color: "#475569" }}>100%</td>
                        </>
                      )}
                      {tablaResumen.mostrarCorteCercano && (
                        <>
                          <td style={{ padding: "8px 12px", textAlign: "center", color: "white", fontWeight: "700" }}>{tablaResumen.totalCorteCercano}</td>
                          <td style={{ padding: "8px 12px", textAlign: "center", color: "#475569" }}>100%</td>
                        </>
                      )}
                      <td style={{ padding: "8px 12px", textAlign: "center", color: "white", fontWeight: "700" }}>{tablaResumen.totalActual}</td>
                      <td style={{ padding: "8px 12px", textAlign: "center", color: "#475569" }}>100%</td>
                      {tablaResumen.mesAnteriorLabel && (
                        <td style={{ padding: "8px 12px", textAlign: "center", color: "white", fontWeight: "700" }}>
                          {tablaResumen.totalActual - tablaResumen.totalAnterior > 0 ? "+" : ""}
                          {tablaResumen.totalActual - tablaResumen.totalAnterior}
                        </td>
                      )}
                    </tr>
                  </tbody>
                </table>
              </div>

              <div style={{ overflowX: "auto" }}>
                <div style={{ display: "flex", gap: "8px", marginBottom: "12px", flexWrap: "wrap" }}>
                  {[
                    { label: "Mes",           value: mesActivo },
                    { label: "Días del mes",  value: `${diasMes}` },
                    { label: "Total horas",   value: `${totalHorasMes} h` },
                    { label: "Min. totales",  value: `${totalMinMes.toLocaleString("es-CO")} min` },
                  ].map((k, i) => (
                    <div key={i} style={{
                      background: i === 0 ? "#fff1f2" : "#f8fafc",
                      border: `1px solid ${i === 0 ? "#fecdd3" : "#e2e8f0"}`,
                      borderRadius: "8px", padding: "8px 12px", minWidth: "92px"
                    }}>
                      <div style={{ fontSize: "9px", color: i === 0 ? C.red : C.slate, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "3px", fontWeight: "700" }}>{k.label}</div>
                      <div style={{ fontSize: "13px", fontWeight: "700", color: i === 0 ? C.red : C.navy, fontFamily: "monospace" }}>{k.value}</div>
                    </div>
                  ))}
                </div>

                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                  <thead>
                    <tr>
                      <th style={thClasif}>Nivel</th>
                      <th style={thClasif}>Disponibilidad</th>
                      <th style={thClasif}>Máx. indisp.</th>
                      <th style={thClasif}>Rango horas</th>
                      <th style={thClasif}>Minutos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {CLASIFICACIONES.map((clas) => {
                      const maxH = clas.dispMin >= 100 ? 0 : (1 - clas.dispMin / 100) * totalHorasMes;
                      const minH = clas.dispMax >= 100 ? 0 : (1 - clas.dispMax / 100) * totalHorasMes;
                      let dispTxt, maxTxt, rangoTxt, minTxt;
                      if (clas.cat === "N0") {
                        dispTxt = "= 100%"; maxTxt = "0 h"; rangoTxt = "0 h"; minTxt = "0 min";
                      } else if (clas.cat === "N6") {
                        dispTxt  = `>= 0% < ${clas.dispMax}%`;
                        maxTxt   = `> ${fmtHMin(minH)}`;
                        rangoTxt = `> ${fmtHMin(minH)}`;
                        minTxt   = `> ${fmtMin(minH)}`;
                      } else {
                        dispTxt  = `>= ${clas.dispMin}% < ${clas.dispMax}%`;
                        maxTxt   = fmtHMin(maxH);
                        rangoTxt = `${fmtHMin(minH)} – ${fmtHMin(maxH)}`;
                        minTxt   = `${fmtMin(minH)} – ${fmtMin(maxH)}`;
                      }
                      return (
                        <tr key={clas.cat} style={{ background: clas.color }}>
                          <td style={{ padding: "7px 10px", textAlign: "center" }}>
                            <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: "4px", background: "rgba(255,255,255,0.18)", color: "#fff", fontWeight: "700", fontSize: "12px" }}>{clas.cat}</span>
                          </td>
                          <td style={{ padding: "7px 10px", color: "#fff", textAlign: "center", fontSize: "11px" }}>{dispTxt}</td>
                          <td style={{ padding: "7px 10px", color: "#fff", textAlign: "center", fontWeight: "600" }}>{maxTxt}</td>
                          <td style={{ padding: "7px 10px", color: "rgba(255,255,255,0.85)", textAlign: "center", fontSize: "11px" }}>{rangoTxt}</td>
                          <td style={{ padding: "7px 10px", color: "rgba(255,255,255,0.85)", textAlign: "center", fontSize: "11px" }}>{minTxt}</td>
                        </tr>
                      );
                    })}
                    <tr style={{ background: C.navy }}>
                      <td colSpan={5} style={{ padding: "6px 10px", fontSize: "10px", color: C.light, textAlign: "center", fontFamily: "monospace" }}>
                        Fórmula: Horas indisp. = (1 − % disp.) × {totalHorasMes} h &nbsp;|&nbsp; {diasMes} días × 24 h = {totalHorasMes} h totales
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: "center", color: C.slate, padding: "30px" }}>Cargando datos...</div>
        )}
      </div>

      {/* ══ GRÁFICA PRIORIDAD ══ */}
      <div style={cardStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
          <div style={{ width: "3px", height: "16px", background: C.red, borderRadius: "2px" }} />
          <h3 style={{ margin: 0, color: C.navy, fontSize: "14px", fontWeight: "600" }}>Prioridad N2+ por mes</h3>
        </div>
        <ResumenGrafica r={resumenGrafica(data, ["alto", "medio"])} labelDominante="Prioridad predominante" />
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <LineChart data={data} margin={{ top: 16, right: 24, left: 4, bottom: 10 }}>
          <CartesianGrid strokeDasharray="2 4" stroke="#eef2f6" vertical={false} />
          <XAxis dataKey="mes" tick={axisStyle} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
          <YAxis tick={axisStyle} axisLine={false} tickLine={false} width={40} />
          <Tooltip content={<CustomTooltipN2 />} />
          <Legend wrapperStyle={{ color: C.navy, fontWeight: "500", fontSize: "13px" }} iconType="circle" />
          <Line type="monotone" dataKey="alto" name="Alto" stroke={C.red} strokeWidth={2.4}
            dot={{ ...dotStyle, stroke: C.red, fill: C.white }} activeDot={activeDot} />
          <Line type="monotone" dataKey="medio" name="Medio" stroke="#3b82f6" strokeWidth={2.4}
            dot={{ ...dotStyle, stroke: "#3b82f6", fill: C.white }} activeDot={activeDot} />
        </LineChart>
        </ResponsiveContainer>
      </div>

      {/* ══ CATEGORÍA + JEFE ══ */}
      <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
        {[
          { title: "Ofensores N2+ por categoría (por mes)", datos: categoriaData, labelDominante: "Categoría con más peso" },
          { title: "Ofensores N2+ por jefe (por mes)",      datos: jefeData,      labelDominante: "Mayor carga actual" },
        ].map((g, gi) => {
          // Para la gráfica de categorías: usar CATEGORIAS_N2MAS para garantizar
          // que N5 y N6 siempre aparezcan aunque el primer mes no tenga datos de ellas.
          // Para la gráfica de jefes: usar todas las claves presentes en los datos.
          const esCategorias = gi === 0;
          const todasLasClaves = esCategorias
            ? CATEGORIAS_N2MAS
            : [...new Set(g.datos.flatMap(d => Object.keys(d).filter(k => k !== "mes")))];
          const coloresCategorias = { N2: C.red, N3: "#3b82f6", N4: "#10b981", N5: C.amber, N6: "#8b5cf6" };
          const paleta = [C.red, "#3b82f6", "#10b981", C.amber, "#8b5cf6", C.orange];
          return (
          <div key={gi} style={{ ...cardStyle, flex: "1 1 400px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
              <div style={{ width: "3px", height: "16px", background: C.red, borderRadius: "2px" }} />
              <h3 style={{ margin: 0, color: C.navy, fontSize: "14px", fontWeight: "600" }}>{g.title}</h3>
            </div>
            <ResumenGrafica r={resumenGrafica(g.datos, todasLasClaves)} labelDominante={g.labelDominante} />
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <LineChart data={g.datos} margin={{ top: 16, right: 24, left: 4, bottom: 10 }}>
              <CartesianGrid strokeDasharray="2 4" stroke="#eef2f6" vertical={false} />
              <XAxis dataKey="mes" tick={axisStyle} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
              <YAxis tick={axisStyle} axisLine={false} tickLine={false} width={40} />
              <Tooltip content={<CustomTooltipN2 />} />
              <Legend wrapperStyle={{ color: C.navy, fontWeight: "500", fontSize: "13px" }} iconType="circle" />
              {g.datos.length > 0 && todasLasClaves.map((key, i) => {
                const color = esCategorias
                  ? (coloresCategorias[key] || paleta[i % paleta.length])
                  : paleta[i % paleta.length];
                return (
                  <Line key={key} type="monotone" dataKey={key} stroke={color} strokeWidth={2.4}
                    dot={{ ...dotStyle, stroke: color, fill: C.white }} activeDot={activeDot} />
                );
              })}
            </LineChart>
            </ResponsiveContainer>
          </div>
          );
        })}
      </div>

      {/* ══ EVOLUCIÓN Y RANKING POR JEFE DE ZONA (sección unificada) ══ */}
      {jefeData.length > 0 && (vistaJefes === "mes" ? rankingJefesSeleccion.length > 0 : (semaforoRankingCorte?.ranking?.length > 0)) && (() => {
        const lista = vistaJefes === "mes" ? rankingJefesSeleccion : (semaforoRankingCorte?.ranking || []);
        const labelAct  = vistaJefes === "mes" ? mesJefeA : semaforoRankingCorte?.fechaActual;
        const labelAnt  = vistaJefes === "mes" ? mesJefeB : semaforoRankingCorte?.fechaCercana;
        const totalActual   = lista.reduce((s, x) => s + (x.actual   || 0), 0);
        const totalAnterior = lista.reduce((s, x) => s + (x.anterior || 0), 0);
        const totalDelta    = totalActual - totalAnterior;
        const totalPct      = totalAnterior > 0 ? ((totalDelta / totalAnterior) * 100).toFixed(1) : null;

        return (
        <div style={cardStyle}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px", flexWrap: "wrap", gap: "10px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div style={{ width: "3px", height: "16px", background: "#8b5cf6", borderRadius: "2px" }} />
              <h3 style={{ margin: 0, color: C.navy, fontSize: "14px", fontWeight: "600" }}>Evolución y Ranking por Jefe de Zona</h3>
              <span style={{ fontSize: "11px", color: C.slate, marginLeft: "4px" }}>
                — {vistaJefes === "mes" ? `Comparativo: ${labelAct} vs ${labelAnt}` : `Corte: ${labelAct} vs ${labelAnt}`}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
              {vistaJefes === "mes" && (
                <span style={{ fontSize: "11px", color: C.light, fontStyle: "italic" }}>
                  usa los meses del filtro de arriba
                </span>
              )}
              <div style={{ display: "flex", gap: "6px" }}>
                {["mes", "corte"].map(v => (
                  <button key={v} onClick={() => setVistaJefes(v)} style={{
                    padding: "5px 14px", borderRadius: "20px", fontSize: "11px", fontWeight: "600", cursor: "pointer",
                    background: vistaJefes === v ? C.navy : "#f1f5f9",
                    color: vistaJefes === v ? "#fff" : C.slate,
                    border: vistaJefes === v ? `1px solid ${C.navy}` : "1px solid #e2e8f0"
                  }}>
                    {v === "mes" ? "📅 Mes a mes" : "📆 Corte a corte"}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <p style={{ margin: "0 0 16px", fontSize: "12px", color: C.light }}>
            Ofensores N2+ por jefe de zona, su variación frente al periodo anterior, y qué tanto pesa cada jefe
            dentro del total: la última columna muestra qué % de todos los ofensores N2+ de {labelAct} le
            corresponde a cada jefe (ej.: 57% = ese jefe concentra el 57% de la carga total de ese mes).
          </p>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, textAlign: "center", width: "40px" }}>#</th>
                  <th style={thStyle}>Jefe de Zona</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>{labelAnt}</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>{labelAct}</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>Variación</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>% Cambio</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>Estado</th>
                  <th style={{ ...thStyle, minWidth: "190px" }}>
                    % de la carga total
                    <div style={{ fontSize: "9px", fontWeight: "400", color: "rgba(255,255,255,0.65)", textTransform: "none", marginTop: "2px" }}>
                      parte del total de {labelAct} que es de este jefe
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {lista.map((s, i) => {
                  const medalColor = i === 0 ? "#f59e0b" : i === 1 ? "#475569" : i === 2 ? "#b45309" : C.slate;
                  const pctTotal   = totalActual > 0 ? Math.round((s.actual / totalActual) * 100) : 0;
                  const barColor   = s.delta > 0 ? `linear-gradient(90deg, ${C.redDark}, ${C.red})` :
                                      s.delta < 0 ? `linear-gradient(90deg, ${C.green}, ${C.greenMid})` :
                                      `linear-gradient(90deg, ${C.slate}, ${C.light})`;
                  return (
                    <tr key={i} style={{ background: i % 2 === 0 ? "#f8fafc" : "#fff", borderBottom: "1px solid #e2e8f0" }}>
                      <td style={{ padding: "10px 12px", textAlign: "center" }}>
                        <span style={{ fontWeight: "700", color: medalColor, fontSize: "14px" }}>
                          {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}
                        </span>
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <span style={{ fontSize: "13px" }}>{s.icon}</span>
                          <span style={{ fontWeight: "600", color: C.navy }}>{s.jefe}</span>
                        </div>
                      </td>
                      <td style={{ padding: "10px 12px", textAlign: "center", color: C.slate }}>{s.anterior || "—"}</td>
                      <td style={{ padding: "10px 12px", textAlign: "center", fontWeight: "700", color: C.navy }}>{s.actual}</td>
                      <td style={{ padding: "10px 12px", textAlign: "center", fontWeight: "700",
                        color: s.delta > 0 ? C.red : s.delta < 0 ? C.greenMid : C.slate }}>
                        {s.delta > 0 ? `+${s.delta}` : s.delta === 0 ? "—" : s.delta}
                      </td>
                      <td style={{ padding: "10px 12px", textAlign: "center", fontSize: "12px",
                        color: s.delta > 0 ? C.red : s.delta < 0 ? C.greenMid : C.slate }}>
                        {s.pct ? `${s.pct > 0 ? "+" : ""}${s.pct}%` : "—"}
                      </td>
                      <td style={{ padding: "10px 12px", textAlign: "center" }}>
                        <span style={{
                          display: "inline-block", padding: "2px 10px", borderRadius: "20px",
                          fontSize: "11px", fontWeight: "600",
                          background: s.delta > 0 ? "rgba(227,6,19,0.1)" : s.delta < 0 ? "rgba(21,128,61,0.1)" : "rgba(100,116,139,0.1)",
                          color: s.delta > 0 ? C.red : s.delta < 0 ? C.green : C.slate
                        }}>
                          {s.icon} {s.estado}
                        </span>
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <div style={{ flex: 1, background: "#eef2f6", borderRadius: "6px", height: "9px", overflow: "hidden" }}>
                            <div style={{ width: `${pctTotal}%`, height: "100%", borderRadius: "6px", background: barColor }} />
                          </div>
                          <span style={{ fontSize: "11px", fontWeight: "700", color: C.navy, minWidth: "30px", textAlign: "right" }}>{pctTotal}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: C.navy }}>
                  <td colSpan={2} style={{ padding: "10px 12px", color: "white", fontWeight: "700", letterSpacing: "0.04em" }}>TOTAL GENERAL</td>
                  <td style={{ padding: "10px 12px", textAlign: "center", color: "white", fontWeight: "700" }}>{totalAnterior}</td>
                  <td style={{ padding: "10px 12px", textAlign: "center", color: "white", fontWeight: "700" }}>{totalActual}</td>
                  <td style={{ padding: "10px 12px", textAlign: "center", fontWeight: "700",
                    color: totalDelta > 0 ? "#fca5a5" : totalDelta < 0 ? "#86efac" : "#cbd5e1" }}>
                    {totalDelta > 0 ? `+${totalDelta}` : totalDelta === 0 ? "—" : totalDelta}
                  </td>
                  <td style={{ padding: "10px 12px", textAlign: "center", fontSize: "12px",
                    color: totalDelta > 0 ? "#fca5a5" : totalDelta < 0 ? "#86efac" : "#cbd5e1" }}>
                    {totalPct ? `${totalPct > 0 ? "+" : ""}${totalPct}%` : "—"}
                  </td>
                  <td style={{ padding: "10px 12px" }}></td>
                  <td style={{ padding: "10px 12px", color: "rgba(255,255,255,0.7)", fontSize: "11px" }}>100% del total</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
        );
      })()}

    </div>
  );
}

export default Home;